import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, setDoc, deleteDoc, updateDoc, Timestamp, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { EventPageBackground } from '@/components/layout/EventPageBackground';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { formatDate, formatCurrency } from '@/lib/utils';
import { matchesRsvpIdentity } from '@/lib/event-utils';
import { VenueInfo } from '@/components/venues/VenueInfo';

// Helper function to format time
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};
import { showSuccess, showError, showInfo, confirmAction, showTextForManualCopy } from '@/lib/swal';
import type { SlickPayResultState } from '@/lib/slickpay-return';
import { copyTextToClipboard } from '@/lib/clipboard-utils';
import {
  normalizeTicketCoursesToFormatA,
  normalizeTicketDataToFormatA,
  DEFAULT_INSTANCE_INDEX,
} from '@/lib/ticket-courses';
import SendInvitesModal from '@/components/rsvp/SendInvitesModal';
import { 
    Calendar, 
    MapPin, 
    Users, 
    CreditCard, 
    Mail, 
    UserPlus, 
    CheckCircle, 
    XCircle,
    Clock,
    Share2,
    ExternalLink,
    Copy,
    Link,
    Trash2,
    User,
    Award,
    History,
    Receipt,
    Edit,
    Plus,
    Save,
    X,
    Info,
    Ticket,
    AlertTriangle,
    QrCode
} from 'lucide-react';
import { RSVP, Event, Invoice, UnitType, TicketType } from '@/types';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { readStoredGuestRsvpToken, storeGuestRsvpToken } from '@/lib/guest-rsvp-token';
import { useRanks } from '@/hooks/useRanks';
import {
    calculateInvoiceAmounts,
    generateInvoiceNumber,
    generateUnitInvoiceNumber,
    getAmountReservedOnInvoice,
    getEffectiveAmountPaid,
} from '@/lib/invoice-utils';
import { computeHostInvoiceRecalc, persistHostInvoiceRecalc } from '@/lib/ensure-invoice-for-rsvp';
import {
    findInvitationEmailConflict,
    getRsvpExpectedAmount,
    findFirstActiveRsvpDoc,
    isActiveInvoiceData,
    isActiveRsvpData,
    mapEventData,
    mapRsvpData,
} from '@/lib/event-utils';
import { buildInvoicePaymentLink } from '@/lib/payment-links';
import { getAvailablePrefixes, getRankAndShortName } from '@/lib/rank-utils';
import { getInviterAccessibleTickets } from '@/lib/inviter-accessible-tickets';
import {
    appendInviteLinkToUrl,
    resolveStoredInviteLinkHref,
    stripMaToShortUrlQuery,
    rsvpLinkBelongsToDashboard,
    inviterPaysForFromRsvpLinkDoc,
    mergePersonalInviteLinkRows,
} from '@/lib/personal-invite-links';
import { formatRsvpFirstName, formatRsvpLastName } from '@/lib/format-rsvp-names';
import { BOOKING_FEE_LABEL, computeCreditRecoveryTotalPounds, getBookingFeePerBillableSlotPounds, getInvoiceCreditRecoveryPounds, isCreditRecoveryEnabledForEvent } from '@/lib/credit-booking-fee';
import { DEFAULT_CREDIT_BOOKING_FEE_PENCE, getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';
import {
    describeRsvpTicketSelection,
    formatRsvpGuestName,
    ticketSubtotalForBreakdownRow,
} from '@/lib/invoice-payment-breakdown';
import QRCode from 'qrcode';

interface PersonalInviteLink {
    id: string;
    ticketIds: string[];
    link: string;
    shortUrl?: string;
    clickCount: number;
    createdAt: Date;
    /** When true, inviter pays ("You pay"); when false, guest pays. */
    inviterPaysFor: boolean;
}

export default function MyRSVPDashboardPage() {
    const { unitId: unitId, eventId, rsvpId } = useParams<{ unitId: string; eventId: string; rsvpId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userProfile, loading: authLoading } = useAuth();
    const { ranks } = useRanks();
    const [loading, setLoading] = useState(true);
    const [rsvp, setRsvp] = useState<RSVP | null>(null);
    const [event, setEvent] = useState<Event | null>(null);
    const [invoice, setInvoice] = useState<Invoice | null>(null);

    const creditBookingFeePence = useMemo(() => getCreditBookingFeePenceForEvent(event), [event]);

    const affiliationRequired = event?.requirePrefixRankUnitInfo ?? true;
    const [invitedGuests, setInvitedGuests] = useState<RSVP[]>([]);
    const [showSendInvitesModal, setShowSendInvitesModal] = useState(false);
    const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
    const [showGetLinkModal, setShowGetLinkModal] = useState(false);
    const [showAddGuestModal, setShowAddGuestModal] = useState(false);
    const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
    const [showEditTicketModal, setShowEditTicketModal] = useState(false);
    const [editingGuest, setEditingGuest] = useState<string | null>(null);
    const [editingMealChoices, setEditingMealChoices] = useState<Record<string, string>>({});
    const [editingGuestTicketId, setEditingGuestTicketId] = useState<string>('');
    const [editingGuestTicketCourses, setEditingGuestTicketCourses] = useState<Record<string, string>>({}); // courseId -> option
    const [editingGuestInviterPaysFor, setEditingGuestInviterPaysFor] = useState<boolean>(true);
    
    // Edit details form state
    const [editDetailsForm, setEditDetailsForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        rank: '',
        additionalRank: '',
        unitName: '',
    });
    const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
    const [currentUnit, setCurrentUnit] = useState<any>(null);
    const [savingDetails, setSavingDetails] = useState(false);
    
    // Edit ticket form state
    // ticketCourseSelections[ticketId][instanceIndex][courseId] = option
    const [ticketCourseSelections, setTicketCourseSelections] = useState<Record<string, Record<number, Record<string, string>>>>({});
    const [courseSelections, setCourseSelections] = useState<Record<string, string>>({});
    const [mealChoice, setMealChoice] = useState('');
    const [allergies, setAllergies] = useState('');
    const [savingTicket, setSavingTicket] = useState(false);
    const [submittingApologyFromModal, setSubmittingApologyFromModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<any>(null);
    
    // Identity verification for non-logged-in users
    // Capability token a guest (no account) presents to the getGuestRsvp/manageGuestRsvp/
    // cancelGuestRsvp callables. Sourced from the `?t=` link param or this browser session.
    const [guestManageToken, setGuestManageToken] = useState<string | null>(null);
    const [needsVerification, setNeedsVerification] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verificationRequiresPrefix, setVerificationRequiresPrefix] = useState(true); // Default to true for safety
    const [verificationData, setVerificationData] = useState({
        name: '',
        rank: '',
        email: '',
    });

    // RSVP cancellation
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancellationReason, setCancellationReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    // Guest addition – guard against double-submit on mobile
    const [addingGuest, setAddingGuest] = useState(false);
    const [resendingInviteGuestId, setResendingInviteGuestId] = useState<string | null>(null);
    const [removingGuestId, setRemovingGuestId] = useState<string | null>(null);

    // QR Code for check-in
    const [showQRCodeDialog, setShowQRCodeDialog] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
    const [qrCodeForGuest, setQrCodeForGuest] = useState<RSVP | null>(null);
    const [selectedInviteTicketId, setSelectedInviteTicketId] = useState('');
    const [inviteLinkInviterPaysFor, setInviteLinkInviterPaysFor] = useState(true);
    const [loadingPersonalLinks, setLoadingPersonalLinks] = useState(false);
    const [generatingPersonalLink, setGeneratingPersonalLink] = useState(false);
    const [personalInviteLinks, setPersonalInviteLinks] = useState<PersonalInviteLink[]>([]);

    const loadInvitedGuests = useCallback(
        async (hostRsvp: RSVP | null | undefined): Promise<void> => {
            if (!unitId || !eventId) {
                setInvitedGuests([]);
                return;
            }

            if (!hostRsvp) {
                setInvitedGuests([]);
                return;
            }

            // Guests can't list the rsvps collection directly (reads require auth). The invited-guest
            // list is supplied by the getGuestRsvp / add/remove/edit callables instead, so skip the
            // client query for hosts viewing as a guest (it would be permission-denied).
            if (!currentUser || guestManageToken) {
                return;
            }

            const identifiers = Array.from(
                new Set(
                    [hostRsvp.userId, hostRsvp.id]
                        .map((value) => (typeof value === 'string' ? value.trim() : ''))
                        .filter((value): value is string => value.length > 0),
                ),
            );

            if (identifiers.length === 0) {
                setInvitedGuests([]);
                return;
            }

            try {
                const guestMap = new Map<string, RSVP>();

                await Promise.all(
                    identifiers.map(async (identifier) => {
                        const guestsQuery = query(
                            collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
                            where('invitedBy', '==', identifier),
                        );
                        const guestsSnapshot = await getDocs(guestsQuery);

                        guestsSnapshot.docs.forEach((guestDoc) => {
                            const mappedGuest = mapRsvpData(guestDoc);
                            if (!mappedGuest) {
                                return;
                            }

                            guestMap.set(mappedGuest.id, mappedGuest);
                        });
                    }),
                );

                setInvitedGuests(Array.from(guestMap.values()));
            } catch (error) {
                console.error('Error fetching invited guests:', error);
                setInvitedGuests([]);
            }
        },
        [unitId, eventId, currentUser, guestManageToken],
    );

    // Manual guest addition form state
    const [newGuestForm, setNewGuestForm] = useState({
        firstName: '',
        lastName: '',
        rank: '',
        additionalRank: '',
        unit: '',
        email: '',
        allergies: '',
        courseSelections: {} as Record<string, string>,
        ticketTypeId: '' as string,
        ticketPrice: 0 as number,
        ticketCourseSelections: {} as Record<string, string>, // courseId -> option
        inviterPaysFor: true,
    });

    const inviterAccessibleTickets = useMemo((): TicketType[] => {
        if (!rsvp || !event?.tickets?.length) {
            return [];
        }
        return getInviterAccessibleTickets(event.tickets, rsvp);
    }, [rsvp, event?.tickets]);

    const editGuestTicketOptions = useMemo((): TicketType[] => {
        if (!event?.tickets?.length) {
            return [];
        }
        const base = inviterAccessibleTickets;
        if (!editingGuest) {
            return base;
        }
        const guest = invitedGuests.find((g) => g.id === editingGuest);
        const rawTickets = guest?.responses?.Tickets;
        const firstEntry = Array.isArray(rawTickets) ? rawTickets[0] : undefined;
        const currentId =
            firstEntry && typeof firstEntry === 'object' && typeof firstEntry.id === 'string'
                ? firstEntry.id.trim()
                : '';
        if (currentId && !base.some((t) => t.id === currentId)) {
            const extra = event.tickets.find((t) => t.id === currentId);
            return extra ? [...base, extra] : base;
        }
        return base;
    }, [event?.tickets, inviterAccessibleTickets, editingGuest, invitedGuests]);

    const sendInvitesTicketOptions = useMemo(
        () =>
            inviterAccessibleTickets.map((t) => ({
                id: t.id,
                name: t.name,
                price: t.price || 0,
            })),
        [inviterAccessibleTickets],
    );

    /** For invite links and invitedBy: prefer account userId, else RSVP doc id, else URL param (stable before rsvp fetch completes). */
    const hostInviterIdentifier = useMemo(() => {
        const u = rsvp?.userId;
        if (typeof u === 'string' && u.trim() !== '') {
            return u.trim();
        }
        const id = rsvp?.id;
        if (typeof id === 'string' && id.trim() !== '') {
            return id.trim();
        }
        if (typeof rsvpId === 'string' && rsvpId.trim() !== '') {
            return rsvpId.trim();
        }
        return '';
    }, [rsvp?.userId, rsvp?.id, rsvpId]);

    /** Prefixes come only from the unit type's configured prefixes (or the event's custom prefixes). */
    const currentUnitTypeForPrefixes = useMemo(() => {
        if (!currentUnit?.typeId) return undefined;
        return unitTypes.find((ut) => ut.id === currentUnit.typeId);
    }, [currentUnit, unitTypes]);

    const prefixSelectOptions = useMemo(
        () => getAvailablePrefixes(event, currentUnitTypeForPrefixes),
        [event, currentUnitTypeForPrefixes],
    );

    const hasConfiguredUnitTypePrefixes = prefixSelectOptions.length > 0;

    // SlickPay Open Banking return: show toast here after the callback redirect, since
    // payment now sends guests back to their RSVP dashboard rather than the invoice page.
    useEffect(() => {
        const state = location.state as SlickPayResultState | null;
        if (!state?.slickpayResult) return;

        const message =
            state.message ||
            (state.slickpayResult === 'success'
                ? 'Your Open Banking payment has been confirmed.'
                : state.slickpayResult === 'pending'
                    ? 'Your Open Banking payment is still confirming. Check back shortly.'
                    : 'Your Open Banking payment did not complete. Please try again.');

        if (state.slickpayResult === 'success') {
            showSuccess(message);
        } else if (state.slickpayResult === 'pending') {
            showInfo(message);
        } else {
            showError(message);
        }

        navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, location.pathname, location.search, navigate]);

    useEffect(() => {
        const fetchData = async () => {
            if (!unitId || !eventId || !rsvpId) {
                showError('Invalid parameters');
                setLoading(false);
                return;
            }

            try {
                // Guests (no account) load their booking through the token-verified getGuestRsvp
                // callable — RSVPs are no longer publicly readable. The capability token comes from the
                // `?t=` link param or this browser session and replaces the old sessionStorage
                // "identity verification". Advanced host actions (inviting/managing extra guests) remain
                // gated to logged-in users below.
                let loadAsGuest = !currentUser;
                if (currentUser) {
                    // A signed-in user may open a booking made as a guest under another email, which their account
                    // can't read directly. If they have the emailed link, load it the way a guest would.
                    const linkToken =
                        new URLSearchParams(location.search).get('t') || readStoredGuestRsvpToken(rsvpId);
                    if (linkToken) {
                        try {
                            await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
                        } catch (directErr) {
                            loadAsGuest = (directErr as { code?: string })?.code === 'permission-denied';
                        }
                    }
                }
                if (loadAsGuest) {
                    const guestToken =
                        new URLSearchParams(location.search).get('t') || readStoredGuestRsvpToken(rsvpId);
                    if (!guestToken) {
                        showError('Please open your booking using the link we emailed you.');
                        setLoading(false);
                        return;
                    }

                    let guestPayload: { rsvp: any; invoice: any; inviter: any; invitedGuests?: RSVP[] } | null = null;
                    try {
                        const res: any = await httpsCallable(functions, 'getGuestRsvp')({
                            unitId,
                            eventId,
                            rsvpId,
                            token: guestToken,
                        });
                        guestPayload = res?.data ?? null;
                    } catch (guestErr) {
                        console.error('Guest RSVP load failed', guestErr);
                        showError('This link is invalid or has expired. Please use the link from your email.');
                        setLoading(false);
                        return;
                    }

                    if (!guestPayload?.rsvp) {
                        showError('RSVP not found. Please check the link and try again.');
                        setLoading(false);
                        return;
                    }

                    storeGuestRsvpToken(rsvpId, guestToken);
                    setGuestManageToken(guestToken);
                    setRsvp(guestPayload.rsvp as RSVP);
                    // The rsvps collection isn't guest-listable; the invited-guest list comes from the callable.
                    setInvitedGuests(Array.isArray(guestPayload.invitedGuests) ? guestPayload.invitedGuests : []);

                    // Load the public event document.
                    const guestEventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
                    if (guestEventDoc.exists()) {
                        const guestEvent = mapEventData(guestEventDoc, unitId) as Event;
                        if (guestEvent.eventStatus === 'deleted') {
                            showError('This event has been deleted and is no longer available.');
                            navigate('/dashboard');
                            return;
                        }
                        if (guestEvent.ticketMode === 'multiTenant') {
                            navigate(
                                `/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/tickets-dashboard?t=${encodeURIComponent(guestToken)}`,
                                { replace: true },
                            );
                            return;
                        }
                        setEvent(guestEvent);
                    }

                    // Invoice summary comes from the callable (guests can't read invoices directly).
                    if (guestPayload.invoice) {
                        setInvoice({
                            ...(guestPayload.invoice as Invoice),
                            createdAt: new Date(),
                            updatedAt: new Date(),
                            remindersSent: [],
                            paymentHistory: [],
                        } as Invoice);
                    } else {
                        setInvoice(null);
                    }

                    setLoading(false);
                    return;
                }

                // Fetch RSVP
                const rsvpRef = doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId);
                console.log('Fetching RSVP:', { unitId, eventId, rsvpId, path: rsvpRef.path });

                const rsvpDoc = await getDoc(rsvpRef);
                console.log('RSVP document fetch result:', { 
                    exists: rsvpDoc.exists(), 
                    id: rsvpDoc.id,
                    hasData: !!rsvpDoc.data(),
                    data: rsvpDoc.exists() ? rsvpDoc.data() : null
                });
                
                if (!rsvpDoc.exists()) {
                    console.error('RSVP document does not exist:', { unitId, eventId, rsvpId });
                    
                    // Try to verify the event exists
                    const eventRef = doc(db, 'units', unitId, 'events', eventId);
                    const eventCheck = await getDoc(eventRef);
                    console.log('Event check:', { exists: eventCheck.exists(), id: eventCheck.id });
                    
                    // Try to list RSVPs to see if any exist
                    try {
                        const rsvpsCollection = collection(db, 'units', unitId, 'events', eventId, 'rsvps');
                        const rsvpsSnapshot = await getDocs(rsvpsCollection);
                        console.log('RSVPs in collection:', rsvpsSnapshot.size, 'documents');
                        if (rsvpsSnapshot.size > 0) {
                            console.log('Available RSVP IDs:', rsvpsSnapshot.docs.map(d => d.id));
                        }
                    } catch (listError) {
                        console.error('Error listing RSVPs:', listError);
                    }
                    
                    showError('RSVP not found. Please check the link and try again.');
                    setLoading(false);
                    return;
                }

                const rawRsvpData = rsvpDoc.data();

                if (!isActiveRsvpData(rawRsvpData)) {
                    const rsvpsCollection = collection(db, 'units', unitId, 'events', eventId, 'rsvps');
                    let replacementDoc: Awaited<ReturnType<typeof getDocs>>['docs'][number] | undefined;

                    if (currentUser?.uid) {
                        const byUserSnapshot = await getDocs(
                            query(rsvpsCollection, where('userId', '==', currentUser.uid)),
                        );
                        replacementDoc = findFirstActiveRsvpDoc(byUserSnapshot.docs);
                    }

                    if (!replacementDoc && rawRsvpData?.email) {
                        const byEmailSnapshot = await getDocs(
                            query(rsvpsCollection, where('email', '==', String(rawRsvpData.email).trim())),
                        );
                        replacementDoc = findFirstActiveRsvpDoc(byEmailSnapshot.docs);
                    }

                    if (replacementDoc && replacementDoc.id !== rsvpId) {
                        navigate(
                            `/unit/${unitId}/event/${eventId}/rsvp/${replacementDoc.id}/dashboard`,
                            { replace: true },
                        );
                        return;
                    }

                    showError('This RSVP has been removed and is no longer available.');
                    setLoading(false);
                    navigate('/dashboard');
                    return;
                }

                const rsvpData = {
                    id: rsvpDoc.id,
                    ...rawRsvpData,
                    createdAt: rawRsvpData.createdAt?.toDate(),
                    updatedAt: rawRsvpData.updatedAt?.toDate(),
                    checkInTime: rawRsvpData.checkInTime?.toDate(),
                    deletedAt: rawRsvpData.deletedAt?.toDate?.(),
                } as RSVP;
                
                console.log('RSVP data loaded:', {
                    id: rsvpData.id,
                    userId: rsvpData.userId,
                    userIdType: typeof rsvpData.userId,
                    userIdLength: rsvpData.userId?.length,
                    name: rsvpData.name,
                    email: rsvpData.email,
                    currentUser: currentUser ? currentUser.uid : 'not logged in'
                });
                
                setRsvp(rsvpData);
                
                // Check if user needs to verify identity
                if (!currentUser) {
                    // Only require login if RSVP was created by a registered user (has non-empty userId)
                    // If userId is empty/undefined/null, allow access after verification (guest RSVP)
                    const hasRegisteredUser = !!(rsvpData.userId && typeof rsvpData.userId === 'string' && rsvpData.userId.trim() !== '');
                    
                    console.log('Access check for non-logged-in user:', {
                        hasRegisteredUser,
                        userId: rsvpData.userId,
                        userIdType: typeof rsvpData.userId,
                        userIdTrimmed: typeof rsvpData.userId === 'string' ? rsvpData.userId.trim() : 'N/A',
                        willRequireLogin: hasRegisteredUser
                    });
                    
                    if (hasRegisteredUser) {
                        console.log('Redirecting to login - RSVP has registered user');
                        showError('Please log in to view this RSVP dashboard.');
                        navigate('/login', { state: { returnTo: `${location.pathname}${location.search}` } });
                        return;
                    }

                    // For guest RSVPs (non-registered users), allow access after verification
                    // Check session storage for verification
                    const verifiedKey = `rsvp_verified_${rsvpId}`;
                    const verified = sessionStorage.getItem(verifiedKey);
                    
                    console.log('Verification check:', {
                        verifiedKey,
                        verified,
                        needsVerification: !verified
                    });
                    
                    // Need to fetch event first to determine if prefix is required for verification
                    // Fetch Event before showing verification form
                    const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
                    if (eventDoc.exists()) {
                        const rawEvent = eventDoc.data();
                        const eventDate = rawEvent.date?.toDate() || new Date();
                        const eventData: Event = {
                            id: eventDoc.id,
                            unitId,
                            name: rawEvent.name,
                            ceremonyType: rawEvent.ceremonyType,
                            date: eventDate,
                            location: rawEvent.location,
                            venueId: rawEvent.venueId,
                            questionnaire: rawEvent.questionnaire || [],
                            courses: rawEvent.courses || [],
                            tickets: rawEvent.tickets || [],
                            ticketMode: rawEvent.ticketMode === 'multiTenant' ? 'multiTenant' : 'mealChoice',
                            maxCapacity: rawEvent.maxCapacity,
                            currentAttendees: rawEvent.currentAttendees || 0,
                            waitlistEnabled: rawEvent.waitlistEnabled || false,
                            waitlist: rawEvent.waitlist || [],
                            rsvpDeadline: rawEvent.rsvpDeadline?.toDate() || new Date(),
                            paymentDeadline: rawEvent.paymentDeadline?.toDate() || eventDate,
                            eventStatus: rawEvent.eventStatus,
                            dresscode: rawEvent.dresscode,
                            customInstructions: rawEvent.customInstructions,
                            isPublic: rawEvent.isPublic ?? false,
                            requirePrefixRankUnitInfo: rawEvent.requirePrefixRankUnitInfo ?? true,
                            useCustomPrefixes: Boolean(rawEvent.useCustomPrefixes),
                            customPrefixes: Array.isArray(rawEvent.customPrefixes)
                                ? rawEvent.customPrefixes.filter(
                                      (p: unknown) => typeof p === 'string' && p.trim() !== '',
                                  )
                                : [],
                            logoUrl: rawEvent.logoUrl,
                            bannerUrl: rawEvent.bannerUrl,
                            backgroundImageUrl: rawEvent.backgroundImageUrl,
                            backgroundColor: rawEvent.backgroundColor,
                            createdBy: rawEvent.createdBy,
                            createdAt: rawEvent.createdAt?.toDate() || new Date(),
                            guestCoversCreditCost: rawEvent.guestCoversCreditCost ?? false,
                            creditBookingFeePence:
                                typeof rawEvent.creditBookingFeePence === 'number'
                                    ? rawEvent.creditBookingFeePence
                                    : undefined,
                            creditCostRecovery:
                                rawEvent.creditCostRecovery &&
                                typeof rawEvent.creditCostRecovery === 'object' &&
                                !Array.isArray(rawEvent.creditCostRecovery)
                                    ? {
                                          enabled: Boolean(
                                              (rawEvent.creditCostRecovery as { enabled?: boolean }).enabled,
                                          ),
                                          amountPence:
                                              typeof (rawEvent.creditCostRecovery as { amountPence?: unknown })
                                                  .amountPence === 'number'
                                                  ? (rawEvent.creditCostRecovery as { amountPence: number })
                                                      .amountPence
                                                  : DEFAULT_CREDIT_BOOKING_FEE_PENCE,
                                      }
                                    : { enabled: false, amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE },
                        };

                        // Check if event is deleted
                        if (eventData.eventStatus === 'deleted') {
                            showError('This event has been deleted and is no longer available.');
                            navigate('/dashboard');
                            return;
                        }

                        // Multi-tenant events use a dedicated bulk-quantity ticket dashboard.
                        if (eventData.ticketMode === 'multiTenant') {
                            navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/tickets-dashboard`, { replace: true });
                            return;
                        }

                        setEvent(eventData);
                        // Set whether prefix is required for verification based on the fetched event
                        setVerificationRequiresPrefix(eventData.requirePrefixRankUnitInfo ?? true);
                    } else {
                        // If event doesn't exist, default to requiring prefix
                        setVerificationRequiresPrefix(true);
                    }
                    
                    if (!verified) {
                        console.log('Setting needsVerification to true - user needs to verify identity');
                        setNeedsVerification(true);
                        setLoading(false);
                        return; // Don't load other data until verified
                    }
                    
                    console.log('Verification passed, continuing to load invoice data');
                } else if (
                    rsvpData.userId &&
                    rsvpData.userId.trim() !== '' &&
                    currentUser.uid !== rsvpData.userId &&
                    !userProfile?.isAdmin
                ) {
                    // User is logged in but not the owner (unless they are an admin)
                    showError('You do not have permission to view this RSVP');
                    navigate('/dashboard');
                    return;
                }

                // For logged-in users, fetch event if not already loaded
                if (!event) {
                    const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
                    if (eventDoc.exists()) {
                        const rawEvent = eventDoc.data();
                        const eventDate = rawEvent.date?.toDate() || new Date();
                        const eventData: Event = {
                            id: eventDoc.id,
                            unitId,
                            name: rawEvent.name,
                            ceremonyType: rawEvent.ceremonyType,
                            date: eventDate,
                            location: rawEvent.location,
                            venueId: rawEvent.venueId,
                            questionnaire: rawEvent.questionnaire || [],
                            courses: rawEvent.courses || [],
                            tickets: rawEvent.tickets || [],
                            ticketMode: rawEvent.ticketMode === 'multiTenant' ? 'multiTenant' : 'mealChoice',
                            maxCapacity: rawEvent.maxCapacity,
                            currentAttendees: rawEvent.currentAttendees || 0,
                            waitlistEnabled: rawEvent.waitlistEnabled || false,
                            waitlist: rawEvent.waitlist || [],
                            rsvpDeadline: rawEvent.rsvpDeadline?.toDate() || new Date(),
                            paymentDeadline: rawEvent.paymentDeadline?.toDate() || eventDate,
                            eventStatus: rawEvent.eventStatus,
                            dresscode: rawEvent.dresscode,
                            customInstructions: rawEvent.customInstructions,
                            isPublic: rawEvent.isPublic ?? false,
                            requirePrefixRankUnitInfo: rawEvent.requirePrefixRankUnitInfo ?? true,
                            useCustomPrefixes: Boolean(rawEvent.useCustomPrefixes),
                            customPrefixes: Array.isArray(rawEvent.customPrefixes)
                                ? rawEvent.customPrefixes.filter(
                                      (p: unknown) => typeof p === 'string' && p.trim() !== '',
                                  )
                                : [],
                            logoUrl: rawEvent.logoUrl,
                            bannerUrl: rawEvent.bannerUrl,
                            backgroundImageUrl: rawEvent.backgroundImageUrl,
                            backgroundColor: rawEvent.backgroundColor,
                            createdBy: rawEvent.createdBy,
                            createdAt: rawEvent.createdAt?.toDate() || new Date(),
                            guestCoversCreditCost: rawEvent.guestCoversCreditCost ?? false,
                            creditBookingFeePence:
                                typeof rawEvent.creditBookingFeePence === 'number'
                                    ? rawEvent.creditBookingFeePence
                                    : undefined,
                            creditCostRecovery:
                                rawEvent.creditCostRecovery &&
                                typeof rawEvent.creditCostRecovery === 'object' &&
                                !Array.isArray(rawEvent.creditCostRecovery)
                                    ? {
                                          enabled: Boolean(
                                              (rawEvent.creditCostRecovery as { enabled?: boolean }).enabled,
                                          ),
                                          amountPence:
                                              typeof (rawEvent.creditCostRecovery as { amountPence?: unknown })
                                                  .amountPence === 'number'
                                                  ? (rawEvent.creditCostRecovery as { amountPence: number })
                                                      .amountPence
                                                  : DEFAULT_CREDIT_BOOKING_FEE_PENCE,
                                      }
                                    : { enabled: false, amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE },
                        };

                        // Check if event is deleted
                        if (eventData.eventStatus === 'deleted') {
                            showError('This event has been deleted and is no longer available.');
                            navigate('/dashboard');
                            return;
                        }

                        // Multi-tenant events use a dedicated bulk-quantity ticket dashboard.
                        if (eventData.ticketMode === 'multiTenant') {
                            navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/tickets-dashboard`, { replace: true });
                            return;
                        }

                        setEvent(eventData);
                    }
                }

                // Fetch Invoice
                if (rsvpData.invoiceId) {
                    const invoiceDoc = await getDoc(
                        doc(db, 'units', unitId, 'events', eventId, 'invoices', rsvpData.invoiceId)
                    );
                    if (invoiceDoc.exists() && isActiveInvoiceData(invoiceDoc.data())) {
                        const invoiceData = {
                            id: invoiceDoc.id,
                            ...invoiceDoc.data(),
                            createdAt: invoiceDoc.data().createdAt?.toDate(),
                            updatedAt: invoiceDoc.data().updatedAt?.toDate(),
                            remindersSent: invoiceDoc.data().remindersSent?.map((d: any) => d.toDate()) || [],
                            paymentHistory: invoiceDoc.data().paymentHistory?.map((p: any) => ({
                                ...p,
                                date: p.date?.toDate(),
                            })) || [],
                        } as Invoice;
                        setInvoice(invoiceData);
                    } else {
                        setInvoice(null);
                    }
                }

                await loadInvitedGuests(rsvpData);

                setLoading(false);
            } catch (error: any) {
                console.error('Error fetching RSVP data:', error);
                console.error('Error details:', {
                    code: error?.code,
                    message: error?.message,
                    unitId,
                    eventId,
                    rsvpId
                });
                
                // Provide more specific error messages
                if (error?.code === 'permission-denied') {
                    showError('You do not have permission to view this RSVP. Please log in if you are the owner.');
                } else if (error?.code === 'not-found') {
                    showError('RSVP not found. Please check the link and try again.');
                } else {
                    showError(`Failed to load RSVP information: ${error?.message || 'Unknown error'}`);
                }
                setLoading(false);
            }
        };

        fetchData();
    }, [unitId, eventId, rsvpId, currentUser, userProfile?.isAdmin, loadInvitedGuests]);

    useEffect(() => {
        if (!unitId || !eventId) return;

        const eventRef = doc(db, 'units', unitId, 'events', eventId);
        const unsub = onSnapshot(eventRef, (snap) => {
            if (!snap.exists()) return;
            setEvent((prev) => {
                const mapped = mapEventData(snap, unitId) as Event;
                return prev ? ({ ...prev, ...mapped, id: eventId, unitId } as Event) : mapped;
            });
        });
        return () => unsub();
    }, [unitId, eventId]);

    useEffect(() => {
        if (!unitId || !eventId || !invoice?.id) return;
        // Guests loaded the invoice summary via getGuestRsvp and cannot subscribe to the invoice
        // document directly (reads are locked down); only authenticated owners get the live listener.
        if (!currentUser || guestManageToken) return;

        const invoiceRef = doc(db, 'units', unitId, 'events', eventId, 'invoices', invoice.id);
        const unsub = onSnapshot(invoiceRef, (snap) => {
            if (!snap.exists()) {
                setInvoice(null);
                return;
            }
            const d = snap.data()!;
            if (!isActiveInvoiceData(d)) {
                setInvoice(null);
                return;
            }
            const invoiceData = {
                id: snap.id,
                ...d,
                createdAt: d.createdAt?.toDate?.(),
                updatedAt: d.updatedAt?.toDate?.(),
                remindersSent: d.remindersSent?.map((x: { toDate?: () => Date }) => x.toDate?.()) || [],
                paymentHistory: (d.paymentHistory || []).map((p: { date?: { toDate?: () => Date } }) => ({
                    ...p,
                    date: p.date?.toDate?.(),
                })),
            } as Invoice;
            setInvoice(invoiceData);
        });
        return () => unsub();
    }, [unitId, eventId, invoice?.id, currentUser]);

    // Apply an edit to THIS booking. Guests (no account) route through the token-verified
    // manageGuestRsvp callable; logged-in owners write directly. `updatedAt`/`userId` are managed
    // server-side for guests and stripped from the change set.
    const applyRsvpUpdate = async (changes: Record<string, any>) => {
        if (!unitId || !eventId || !rsvpId) return;
        if (guestManageToken) {
            // updatedAt/userId are managed server-side; status is passed as its own callable arg.
            const { updatedAt: _updatedAt, userId: _userId, status, ...allowed } = changes;
            void _updatedAt;
            void _userId;
            await httpsCallable(functions, 'manageGuestRsvp')({
                unitId,
                eventId,
                rsvpId,
                token: guestManageToken,
                changes: allowed,
                ...(typeof status === 'string' ? { status } : {}),
            });
            return;
        }
        await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId), changes);
    };

    // Cancel THIS booking. Guests route through cancelGuestRsvp (token-verified); owners write the
    // status directly. Either way the onRsvpCancelled trigger reconciles invoice/attendee/email index.
    const applyRsvpCancellation = async (reason?: string) => {
        if (!unitId || !eventId || !rsvpId) return;
        if (guestManageToken) {
            await httpsCallable(functions, 'cancelGuestRsvp')({
                unitId,
                eventId,
                rsvpId,
                token: guestManageToken,
                reason: reason ?? '',
            });
            return;
        }
        await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId), {
            status: 'cancelled',
            cancellationReason: reason?.trim() || null,
            cancelledAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
    };

    /** Apply the { invoice, invitedGuests } returned by the add/remove/edit guest callables. */
    const applyGuestMutationResult = (data: unknown) => {
        const payload = data as {
            invoice?: {
                totalAmount: number;
                outstandingBalance: number;
                status: Invoice['status'];
                rsvps: string[];
            } | null;
            invitedGuests?: RSVP[];
        } | undefined;
        const updatedInvoice = payload?.invoice;
        if (updatedInvoice && invoice) {
            setInvoice({
                ...invoice,
                totalAmount: updatedInvoice.totalAmount,
                outstandingBalance: updatedInvoice.outstandingBalance,
                status: updatedInvoice.status,
                rsvps: updatedInvoice.rsvps,
            });
        }
        if (Array.isArray(payload?.invitedGuests)) {
            setInvitedGuests(payload.invitedGuests);
        }
    };

    // Check SumUp payment status if RSVP has "Payment Started" status and payment_method is "sumup"
    useEffect(() => {
        const checkSumUpPayment = async () => {
            if (!rsvp || !unitId || !eventId) return;

            const paymentStatus = (rsvp as any).paymentStatus;
            const paymentMethod = (rsvp as any).paymentMethod;
            const checkoutId = (rsvp as any).sumupCheckoutId;

            if (paymentMethod === 'sumup' && paymentStatus === 'Payment Started' && checkoutId) {
                try {
                    const checkSumUpPaymentStatus = httpsCallable(functions, 'checkSumUpPaymentStatus');
                    const result = await checkSumUpPaymentStatus({
                        unitId,
                        eventId,
                        rsvpId: rsvp.id,
                        checkoutId,
                    });

                    const data = result.data as {
                        status: string;
                        updatedStatus?: string;
                        paid?: boolean;
                    };

                    // Update RSVP if payment status changed
                    if (data.updatedStatus && data.updatedStatus !== paymentStatus) {
                        setRsvp({
                            ...rsvp,
                            paymentStatus: data.updatedStatus,
                            paid: data.paid || rsvp.paid,
                        } as RSVP);
                    }
                } catch (error) {
                    console.error('Error checking SumUp payment status:', error);
                    // Silently fail - don't disrupt user experience
                }
            }
        };

        checkSumUpPayment();
    }, [rsvp, unitId, eventId]);

    // Poll Open Banking (SlickPay) when payment is in flight — mirrors dashboard backstop.
    useEffect(() => {
        const checkSlickPayPayment = async () => {
            if (!rsvp || !unitId || !eventId) return;

            const paymentStatus = (rsvp as { paymentStatus?: string }).paymentStatus;
            const paymentMethod = (rsvp as { paymentMethod?: string }).paymentMethod;
            const slickpayInvoiceId = (rsvp as { slickpayInvoiceId?: string }).slickpayInvoiceId;

            if (
                paymentMethod === 'slickpay' &&
                paymentStatus === 'Payment Started' &&
                slickpayInvoiceId
            ) {
                try {
                    const checkSlickPayPaymentStatus = httpsCallable(functions, 'checkSlickPayPaymentStatus');
                    const result = await checkSlickPayPaymentStatus({
                        unitId,
                        eventId,
                        rsvpId: rsvp.id,
                        slickpayInvoiceId,
                    });

                    const data = result.data as {
                        status: string;
                        updatedStatus?: string;
                        paid?: boolean;
                    };

                    if (data.updatedStatus && data.updatedStatus !== paymentStatus) {
                        setRsvp({
                            ...rsvp,
                            paymentStatus: data.updatedStatus,
                            paid: data.paid || rsvp.paid,
                        } as RSVP);
                    }
                } catch (error) {
                    console.error('Error checking SlickPay payment status:', error);
                }
            }
        };

        void checkSlickPayPayment();
        const interval = setInterval(() => {
            void checkSlickPayPayment();
        }, 30_000);

        return () => clearInterval(interval);
    }, [rsvp, unitId, eventId]);

    // Fetch unit types
    useEffect(() => {
        const fetchUnitTypes = async () => {
            try {
                const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
                if (settingsDoc.exists()) {
                    const data = settingsDoc.data();
                    setUnitTypes(data.unitTypes || []);
                }
            } catch (error) {
                console.error('Error fetching unit types:', error);
            }
        };

        fetchUnitTypes();
    }, []);

    // Fetch current unit (for logo display - public info)
    useEffect(() => {
        const fetchCurrentUnit = async () => {
            if (!unitId) return;
            
            try {
                const unitDoc = await getDoc(doc(db, 'units', unitId));
                if (unitDoc.exists()) {
                    setCurrentUnit({
                        id: unitDoc.id,
                        ...unitDoc.data()
                    });
                }
            } catch (error) {
                console.error('Error fetching current unit:', error);
                // Silently fail - unit logo is optional
            }
        };

        fetchCurrentUnit();
    }, [unitId]);

    // Sync payment state when entering edit mode for a guest
    useEffect(() => {
        if (editingGuest) {
            const guest = invitedGuests.find(g => g.id === editingGuest);
            if (guest) {
                const inviterPaysFor = guest.inviterPaysFor !== undefined ? guest.inviterPaysFor : true;
                setEditingGuestInviterPaysFor(inviterPaysFor);
            }
        }
    }, [editingGuest, invitedGuests]);

    const handleOpenEditDetailsModal = () => {
        if (rsvp) {
            let firstName = (rsvp.firstName ?? '').trim();
            let lastName = (rsvp.lastName ?? '').trim();
            if (!firstName && !lastName) {
                const full = (rsvp.name ?? '').trim();
                const spaceIdx = full.indexOf(' ');
                if (spaceIdx === -1) {
                    firstName = full;
                } else {
                    firstName = full.slice(0, spaceIdx).trim();
                    lastName = full.slice(spaceIdx + 1).trim();
                }
            }
            setEditDetailsForm({
                firstName,
                lastName,
                email: rsvp.email,
                rank: rsvp.rank,
                additionalRank: rsvp.additionalRank || '',
                unitName: rsvp.unit,
            });
            setShowEditDetailsModal(true);
        }
    };

    const handleSaveDetails = async () => {
        if (!unitId || !eventId || !rsvpId || !rsvp) {
            showError('Invalid RSVP');
            return;
        }

        const trimmedFirstName = editDetailsForm.firstName.trim();
        const trimmedLastName = editDetailsForm.lastName.trim();
        const trimmedEmail = editDetailsForm.email.trim();
        const normalizedRank = editDetailsForm.rank?.trim() || '';
        const normalizedAdditionalRank = editDetailsForm.additionalRank?.trim() || '';
        const normalizedUnitName = editDetailsForm.unitName?.trim() || '';
        const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();

        if (!trimmedFirstName || !trimmedLastName || !trimmedEmail) {
            showError('Please fill in all required fields');
            return;
        }

        setSavingDetails(true);
        try {
            const updateData: any = {
                name: fullName,
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                unit: normalizedUnitName || null,
                email: trimmedEmail,
                updatedAt: Timestamp.now(),
            };
            
            if (affiliationRequired) {
                const currentUnitTypeForRank = currentUnit
                    ? unitTypes.find((ut) => ut.id === currentUnit.typeId)
                    : undefined;

                const { rank: resolvedRank, rankShortName } = getRankAndShortName({
                    rankName: normalizedRank,
                    currentUnitType: currentUnitTypeForRank,
                    globalRanks: ranks,
                });

                updateData.rank = resolvedRank;
                updateData.rankShortName = rankShortName ?? null;
                // Always persist additionalRank explicitly so it can be cleared
                updateData.additionalRank = normalizedAdditionalRank || null;
            } else {
                updateData.rank = null;
                updateData.rankShortName = null;
                updateData.additionalRank = normalizedAdditionalRank || null;
            }

            await applyRsvpUpdate(updateData);

            // Update local state to match what we wrote to Firestore
            setRsvp({
                ...rsvp,
                name: fullName,
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                email: trimmedEmail,
                rank: normalizedRank || '',
                additionalRank: normalizedAdditionalRank || '',
                unit: normalizedUnitName || '',
                rankShortName: updateData.rankShortName ?? undefined,
                updatedAt: updateData.updatedAt,
            } as RSVP);

            showSuccess('Personal details updated successfully!');
            setShowEditDetailsModal(false);
        } catch (error) {
            console.error('Error updating RSVP:', error);
            showError('Failed to update RSVP. Please try again.');
        } finally {
            setSavingDetails(false);
        }
    };

    const handleOpenEditTicketModal = () => {
        if (!rsvp || !event) return;
        
        // Load existing ticket data
        if (rsvp.responses?.Tickets && Array.isArray(rsvp.responses.Tickets) && rsvp.responses.Tickets.length > 0) {
            // Get the first ticket (assuming user has one ticket)
            const ticket = rsvp.responses.Tickets[0];
            setSelectedTicket(ticket);
            
            const normalizedTc = normalizeTicketCoursesToFormatA(
              rsvp.responses.TicketCourses
            );
            if (normalizedTc[ticket.id]) {
              setTicketCourseSelections({ [ticket.id]: normalizedTc[ticket.id] });
            } else {
              setTicketCourseSelections({});
            }
        } else if (event.courses && event.courses.length > 0) {
            // Load legacy course selections
            const selections: Record<string, string> = {};
            event.courses.forEach((course) => {
                selections[course.id] = rsvp.responses?.[course.name] || '';
            });
            setCourseSelections(selections);
        } else {
            setMealChoice(rsvp.responses?.Meal || '');
        }
        
        setAllergies(rsvp.responses?.Allergies || '');
        setShowEditTicketModal(true);
    };

    const handleTicketTypeChange = (ticketId: string) => {
        if (!event?.tickets) return;
        
        const ticketType = event.tickets.find(t => t.id === ticketId);
        if (!ticketType) return;
        
        // Create new ticket object with quantity 1
        const newTicket = {
            id: ticketType.id,
            name: ticketType.name,
            price: ticketType.price,
            qty: 1
        };
        
        setSelectedTicket(newTicket);
        
        // Load existing course selections for this ticket type if available
        if (ticketCourseSelections[ticketId]) {
            // Keep existing selections
        } else {
            // Initialize empty course selections for this ticket
            const instanceIndex = 1;
            const initialSelections: Record<string, string> = {};
            if (ticketType.courses) {
                ticketType.courses.forEach((course) => {
                    initialSelections[course.id] = '';
                });
            }
            
            setTicketCourseSelections({
                ...ticketCourseSelections,
                [ticketId]: {
                    [instanceIndex]: initialSelections
                }
            });
        }
    };

    const handleSaveTicket = async () => {
        if (!unitId || !eventId || !rsvpId || !rsvp || !event) {
            showError('Invalid RSVP');
            return;
        }

        setSavingTicket(true);
        try {
            // Build responses object
            const responses: Record<string, any> = {
                ...rsvp.responses,
                Allergies: allergies,
            };

            // Update ticket course selections or legacy course/meal selections
            if (event.tickets && event.tickets.length > 0 && selectedTicket) {
                const allowedOwnTicketIds = new Set(inviterAccessibleTickets.map((t) => t.id));
                if (!allowedOwnTicketIds.has(selectedTicket.id)) {
                    showError('You cannot select that ticket type.');
                    setSavingTicket(false);
                    return;
                }
                // Update the ticket in the Tickets array
                const updatedTickets = [{
                    id: selectedTicket.id,
                    name: selectedTicket.name,
                    price: selectedTicket.price,
                    qty: 1
                }];
                responses.Tickets = updatedTickets;
                
                // Find ticket type
                const ticketType = event.tickets.find(t => t.id === selectedTicket.id);
                
                // Update ticket course selections
                if (ticketCourseSelections[selectedTicket.id]) {
                    // Validate all courses are selected
                    if (ticketType?.courses && ticketType.courses.length > 0) {
                        const instanceSelections = ticketCourseSelections[selectedTicket.id];
                        const instanceIndex = 1; // Assuming single ticket instance
                        const selections = instanceSelections[instanceIndex] || {};
                        
                        for (const course of ticketType.courses) {
                            if (!selections[course.id]) {
                                showError('Please select options for all courses');
                                setSavingTicket(false);
                                return;
                            }
                        }
                        
                        const existingNormalized = normalizeTicketCoursesToFormatA(
                          rsvp.responses?.TicketCourses
                        );
                        responses.TicketCourses = {
                          ...existingNormalized,
                          [selectedTicket.id]: ticketCourseSelections[selectedTicket.id],
                        };
                    }
                } else if (ticketType?.courses && ticketType.courses.length > 0) {
                    // If no selections exist but courses are required, show error
                    showError('Please select options for all courses');
                    setSavingTicket(false);
                    return;
                }
            } else if (event.courses && event.courses.length > 0) {
                // Validate course selections
                const allCoursesSelected = event.courses.every(
                    (course) => courseSelections[course.id]?.trim()
                );
                if (!allCoursesSelected) {
                    showError('Please select an option for all courses');
                    setSavingTicket(false);
                    return;
                }
                
                // Update course selections
                event.courses.forEach((course) => {
                    responses[course.name] = courseSelections[course.id];
                });
            } else if (!mealChoice) {
                showError('Please select your meal choice');
                setSavingTicket(false);
                return;
            } else {
                responses.Meal = mealChoice;
            }

            const updatePayload: Record<string, any> = {
                responses,
                updatedAt: Timestamp.now(),
                apologies: false,
            };

            if (rsvp.apologies && rsvp.status === 'declined') {
                updatePayload.status = 'confirmed';
            }

            await applyRsvpUpdate(updatePayload);
            // currentAttendees is recounted server-side (syncEventAttendeeCount trigger).

            // Update local state
            setRsvp({
                ...rsvp,
                responses,
                apologies: false,
                status: (updatePayload.status as RSVP['status']) ?? rsvp.status,
            } as RSVP);

            showSuccess(
                rsvp.apologies
                    ? 'Apologies removed. You are now marked as attending.'
                    : 'Ticket and meal preferences updated successfully!'
            );
            setShowEditTicketModal(false);
        } catch (error) {
            console.error('Error updating RSVP:', error);
            showError('Failed to update RSVP. Please try again.');
        } finally {
            setSavingTicket(false);
        }
    };

    const handleSubmitApologyFromModal = async () => {
        if (!unitId || !eventId || !rsvpId || !rsvp) {
            showError('Invalid RSVP');
            return;
        }

        const result = await confirmAction({
            title: 'Record Apologies?',
            text: 'We will mark you as unable to attend this event. You can still invite guests if needed.',
            confirmButtonText: 'Record Apologies',
            confirmButtonColor: '#d97706',
        });

        if (!result.isConfirmed) {
            return;
        }

        setSubmittingApologyFromModal(true);
        try {
            await applyRsvpUpdate({
                apologies: true,
                status: 'declined',
                updatedAt: Timestamp.now(),
            });

            // currentAttendees is recounted server-side (syncEventAttendeeCount trigger).

            setRsvp({
                ...rsvp,
                apologies: true,
                status: 'declined',
            } as RSVP);

            showSuccess('Apologies recorded. Feel free to invite guests if needed.');
            setShowEditTicketModal(false);
        } catch (error) {
            console.error('Error recording apologies:', error);
            showError('Failed to record apologies. Please try again.');
        } finally {
            setSubmittingApologyFromModal(false);
        }
    };

    const handleVerifyIdentity = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!rsvp || !rsvpId) return;

        setVerifying(true);
        
        try {
            if (
                matchesRsvpIdentity(verificationData, rsvp, {
                    requiresPrefix: verificationRequiresPrefix,
                })
            ) {
                // Store verification in session storage
                const verifiedKey = `rsvp_verified_${rsvpId}`;
                sessionStorage.setItem(verifiedKey, 'true');
                
                setNeedsVerification(false);
                showSuccess('Identity verified! You can now view your RSVP dashboard.');
                
                // Reload the page data
                window.location.reload();
            } else {
                showError('The information provided does not match this RSVP. Please check your details and try again.');
            }
        } finally {
            setVerifying(false);
        }
    };

    type CreateShortRsvpLinkResult =
        | {
              ok: true;
              shortUrl: string;
              shortenerUsed: boolean;
              targetUrl?: string;
              linkId?: string;
          }
        | {
              ok: false;
              shortUrl: string;
              shortenerUsed: false;
              targetUrl: string;
              linkId?: string;
          };

    const createShortRsvpLink = async (
        ticketIds: string[] = [],
        inviterPaysForGuest: boolean,
    ): Promise<CreateShortRsvpLinkResult> => {
        if (!unitId || !eventId) {
            throw new Error('Missing unit or event context');
        }

        const fallbackUrl = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp${ticketIds.length ? `?tickets=${ticketIds.join(',')}` : ''}`;
        try {
            const createShortLink = httpsCallable(functions, 'createShortRsvpLink');
            const response = await createShortLink({
                unitId,
                eventId,
                ticketIds,
                origin: window.location.origin,
                inviterPaysFor: inviterPaysForGuest,
                invitedBy: hostInviterIdentifier.trim(),
                ...(rsvpId ? { inviterRsvpId: rsvpId } : {}),
            });
            const result = response.data as {
                shortUrl?: string;
                targetUrl?: string;
                shortenerUsed?: boolean;
                linkId?: string;
            };
            const rawShort = typeof result.shortUrl === 'string' ? result.shortUrl.trim() : '';
            const shortUrlForUi =
                rawShort.includes('ma-to.co.uk')
                    ? stripMaToShortUrlQuery(rawShort)
                    : rawShort || (result.targetUrl ?? fallbackUrl);
            return {
                ok: true,
                shortUrl: shortUrlForUi,
                shortenerUsed: result.shortenerUsed === true,
                targetUrl: result.targetUrl || fallbackUrl,
                linkId: result.linkId,
            };
        } catch (error) {
            console.warn('[invite-links] createShortRsvpLink callable failed (using local fallback)', error);
            const persisted = await persistClientInviteLink(ticketIds, inviterPaysForGuest, fallbackUrl);
            if (!persisted) {
                throw error;
            }
            return {
                ok: false,
                shortUrl: persisted.targetUrl,
                shortenerUsed: false,
                targetUrl: persisted.targetUrl,
                linkId: persisted.linkId,
            };
        }
    };

    const persistClientInviteLink = async (
        ticketIds: string[],
        inviterPaysForGuest: boolean,
        baseUrl: string,
    ): Promise<{ linkId: string; targetUrl: string } | null> => {
        if (!currentUser || !unitId || !eventId) {
            return null;
        }
        const inviterIdentifier = hostInviterIdentifier.trim();
        if (!inviterIdentifier) {
            return null;
        }

        const linkRef = doc(collection(db, 'units', unitId, 'events', eventId, 'rsvpLinks'));
        const targetUrl = appendInviteLinkToUrl(baseUrl, linkRef.id, inviterIdentifier);
        await setDoc(linkRef, {
            ticketIds,
            link: targetUrl,
            targetUrl,
            shortUrl: targetUrl,
            clickCount: 0,
            createdBy: currentUser.uid,
            invitedBy: inviterIdentifier,
            inviterPaysFor: inviterPaysForGuest,
            ...(rsvpId ? { inviterRsvpId: rsvpId } : {}),
            createdAt: Timestamp.now(),
            shortenerProvider: 'ma-to',
            shortenerUsed: false,
        });
        return { linkId: linkRef.id, targetUrl };
    };

    const logInviteLinks = (step: string, detail?: Record<string, unknown>) => {
        console.log(`[invite-links] ${step}`, detail ?? {});
    };

    const loadPersonalInviteLinks = useCallback(async (options?: { silent?: boolean }) => {
        if (!unitId || !eventId) {
            logInviteLinks('load skipped: missing unitId or eventId', { unitId, eventId });
            return;
        }
        if (!currentUser?.uid && !rsvpId) {
            logInviteLinks('load skipped: no currentUser.uid and no rsvpId', {
                hasUid: !!currentUser?.uid,
                rsvpId,
            });
            return;
        }
        if (authLoading) {
            logInviteLinks('load skipped: auth still loading');
            return;
        }

        const inviterIdentifier = hostInviterIdentifier || currentUser?.uid || '';

        const rsvpLinksCollectionPath = `units/${unitId}/events/${eventId}/rsvpLinks`;

        logInviteLinks('load start', {
            silent: options?.silent === true,
            path: rsvpLinksCollectionPath,
            rsvpId,
            hostInviterIdentifier,
            authUid: currentUser?.uid ?? null,
            authLoading,
        });

        setLoadingPersonalLinks(true);
        try {
            const linksRef = collection(db, 'units', unitId, 'events', eventId, 'rsvpLinks');
            let snapshot;
            let usedOrderBy = true;
            try {
                snapshot = await getDocs(query(linksRef, orderBy('createdAt', 'desc')));
            } catch (orderErr) {
                console.warn('rsvpLinks orderBy(createdAt) failed, loading unsorted', orderErr);
                usedOrderBy = false;
                snapshot = await getDocs(linksRef);
            }

            const totalInCollection =
                typeof (snapshot as { size?: number }).size === 'number'
                    ? (snapshot as { size: number }).size
                    : undefined;
            logInviteLinks('firestore snapshot', {
                usedOrderBy,
                size: totalInCollection,
                path: rsvpLinksCollectionPath,
            });

            const links: PersonalInviteLink[] = [];
            let skippedOther = 0;
            const skippedSample: Array<{
                docId: string;
                createdBy: unknown;
                inviterRsvpId: unknown;
            }> = [];
            snapshot.forEach((d) => {
                const data = d.data() as Record<string, unknown>;
                if (
                    !rsvpLinkBelongsToDashboard(data, {
                        authUid: currentUser?.uid,
                        rsvpId,
                    })
                ) {
                    skippedOther += 1;
                    if (skippedSample.length < 8) {
                        skippedSample.push({
                            docId: d.id,
                            createdBy: data.createdBy ?? null,
                            inviterRsvpId: data.inviterRsvpId ?? null,
                        });
                    }
                    return;
                }
                const createdRaw = data.createdAt as { toDate?: () => Date } | undefined;
                const createdAt =
                    createdRaw && typeof createdRaw.toDate === 'function'
                        ? createdRaw.toDate()
                        : new Date();
                const inviterPaysForStored = inviterPaysForFromRsvpLinkDoc(data);
                links.push({
                    id: d.id,
                    ticketIds: Array.isArray(data.ticketIds) ? (data.ticketIds as string[]) : [],
                    link: resolveStoredInviteLinkHref(
                        {
                            shortUrl: data.shortUrl,
                            link: data.link,
                            targetUrl: data.targetUrl,
                        },
                        d.id,
                        inviterIdentifier || rsvpId || '',
                    ),
                    shortUrl: data.shortUrl as string | undefined,
                    clickCount: typeof data.clickCount === 'number' ? data.clickCount : 0,
                    createdAt,
                    inviterPaysFor: inviterPaysForStored,
                });
            });

            links.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
            logInviteLinks('after filter', {
                matchedCount: links.length,
                skippedCount: skippedOther,
                matchedIds: links.map((l) => l.id),
                skippedSample,
                expectedAuthUid: currentUser?.uid ?? null,
                expectedRsvpId: rsvpId ?? null,
                skippedMoreThanSample: skippedOther > skippedSample.length,
            });

            // Merge with previous rows: server wins for same id; keep optimistic rows not yet in snapshot (race after generate).
            setPersonalInviteLinks((prev) => {
                const merged = mergePersonalInviteLinkRows(prev, links);
                logInviteLinks('state merge', {
                    prevCount: prev.length,
                    serverCount: links.length,
                    mergedCount: merged.length,
                    mergedIds: merged.map((m) => m.id),
                });
                return merged;
            });
        } catch (error) {
            console.error('[invite-links] Error loading personal invite links:', error);
            if (!options?.silent) {
                showError('Failed to load your previous links.');
            }
        } finally {
            setLoadingPersonalLinks(false);
            logInviteLinks('load finished');
        }
    }, [unitId, eventId, currentUser, rsvpId, hostInviterIdentifier, authLoading]);

    useEffect(() => {
        if (!unitId || !eventId || !rsvpId || authLoading) {
            return;
        }
        logInviteLinks('effect: prefetch (silent)');
        void loadPersonalInviteLinks({ silent: true });
    }, [unitId, eventId, rsvpId, authLoading, loadPersonalInviteLinks]);

    useEffect(() => {
        if (!showGetLinkModal || !unitId || !eventId) {
            return;
        }
        if (!rsvpId) {
            return;
        }
        if (authLoading) {
            return;
        }
        logInviteLinks('effect: modal open → load');
        void loadPersonalInviteLinks();
    }, [showGetLinkModal, currentUser, unitId, eventId, rsvpId, authLoading, loadPersonalInviteLinks]);

    const handleOpenGetLinkModal = () => {
        logInviteLinks('open Get Link modal', {
            inviterAccessibleTicketsCount: inviterAccessibleTickets.length,
        });
        if (inviterAccessibleTickets.length > 0) {
            setSelectedInviteTicketId((prev) => prev || inviterAccessibleTickets[0].id);
        }
        setShowGetLinkModal(true);
    };

    const handleGeneratePersonalInviteLink = async () => {
        const inviterIdentifier = hostInviterIdentifier;
        if (!unitId || !eventId || !inviterIdentifier) return;
        if (!currentUser) {
            showError('Please log in (or create an account) to generate personal invite links.');
            return;
        }
        if (!selectedInviteTicketId) {
            showError('Please select a ticket type.');
            return;
        }
        if (!inviterAccessibleTickets.some((t) => t.id === selectedInviteTicketId)) {
            showError('Selected ticket is not available to you.');
            return;
        }

        const ticketIds = [selectedInviteTicketId];
        const fallbackBase = `${window.location.origin}/unit/${unitId}/event/${eventId}/rsvp?tickets=${ticketIds.join(',')}`;

        setGeneratingPersonalLink(true);
        try {
            const shortResult = await createShortRsvpLink(ticketIds, inviteLinkInviterPaysFor);
            logInviteLinks('generate: callable result', {
                ok: shortResult.ok,
                linkId: shortResult.linkId,
                shortenerUsed: shortResult.shortenerUsed,
                hasShortUrl: !!shortResult.shortUrl,
            });

            const linkIdForReload = shortResult.linkId;

            const personalizedLink = resolveStoredInviteLinkHref(
                { shortUrl: shortResult.shortUrl, targetUrl: shortResult.targetUrl },
                linkIdForReload || `local-${Date.now()}`,
                inviterIdentifier,
            );

            setPersonalInviteLinks((prev) => [
                {
                    id: linkIdForReload || `local-${Date.now()}`,
                    ticketIds,
                    link: personalizedLink,
                    shortUrl: shortResult.shortUrl,
                    clickCount: 0,
                    createdAt: new Date(),
                    inviterPaysFor: inviteLinkInviterPaysFor,
                },
                ...prev,
            ]);

            const copied = await copyTextToClipboard(personalizedLink);
            if (copied) {
                showSuccess(
                    shortResult.shortenerUsed
                        ? 'Short invitation link copied to clipboard!'
                        : 'Invitation link copied to clipboard',
                );
            } else {
                await showTextForManualCopy('Copy your invitation link', personalizedLink);
            }
            if (currentUser && linkIdForReload) {
                logInviteLinks('generate: reloading list after write');
                await loadPersonalInviteLinks();
            } else {
                logInviteLinks('generate: skip reload', {
                    hasCurrentUser: !!currentUser,
                    linkId: linkIdForReload ?? null,
                });
            }
        } catch (_error) {
            logInviteLinks('generate: unexpected error', {
                error: _error instanceof Error ? _error.message : String(_error),
            });
            const persisted = await persistClientInviteLink(
                ticketIds,
                inviteLinkInviterPaysFor,
                fallbackBase,
            );
            if (!persisted) {
                showError('Failed to generate invitation link. Please try again.');
                return;
            }
            const copied = await copyTextToClipboard(persisted.targetUrl);
            setPersonalInviteLinks((prev) => [
                {
                    id: persisted.linkId,
                    ticketIds,
                    link: persisted.targetUrl,
                    shortUrl: undefined,
                    clickCount: 0,
                    createdAt: new Date(),
                    inviterPaysFor: inviteLinkInviterPaysFor,
                },
                ...prev,
            ]);
            if (copied) {
                showSuccess('Invitation link copied to clipboard');
            } else {
                await showTextForManualCopy('Copy your invitation link', persisted.targetUrl);
            }
        } finally {
            setGeneratingPersonalLink(false);
        }
    };

    const handleCopyExistingPersonalLink = async (link: PersonalInviteLink) => {
        const inviterIdentifier = hostInviterIdentifier;
        if (!inviterIdentifier) return;
        const personalized = resolveStoredInviteLinkHref(
            { shortUrl: link.shortUrl, link: link.link },
            link.id,
            inviterIdentifier,
        );
        const copied = await copyTextToClipboard(personalized);
        if (copied) {
            showSuccess('Link copied to clipboard!');
        } else {
            await showTextForManualCopy('Copy your invitation link', personalized);
        }
    };

    const handleSharePaymentLink = async (guestEmail: string, guestName: string) => {
        // In a real implementation, this would send an email via backend
        showSuccess(`Payment link will be sent to ${guestName}`);
    };

    const handleSendInvites = async (
        guests: Array<{
            email: string;
            firstName: string;
            lastName: string;
            name: string;
            inviterPaysFor: boolean;
            inviteTicketId: string;
        }>,
        message: string
    ) => {
        if (!unitId || !eventId || !rsvpId) {
            showError('Missing event or RSVP information. Please refresh the page and try again.');
            throw new Error('Missing required parameters for sending invitations');
        }
        if (!currentUser) {
            showError('Please log in (or create an account) to invite additional guests.');
            throw new Error('Guest hosts must be logged in to send invitations');
        }

        try {
            if (event?.tickets && event.tickets.length > 0) {
                const allowed = new Set(inviterAccessibleTickets.map((t) => t.id));
                for (const guest of guests) {
                    const tid = guest.inviteTicketId?.trim();
                    if (!tid || !allowed.has(tid)) {
                        showError('Each guest must have a valid invitation ticket that you are allowed to use.');
                        throw new Error('Invalid invitation ticket selection');
                    }
                }
            }

            // Hosts can't read other attendees' RSVPs, so the "already on this event" check runs
            // server-side; duplicates within this batch are still caught locally.
            let emailConflict = findInvitationEmailConflict(guests.map((guest) => guest.email), []);
            if (!emailConflict) {
                const findEventRsvpEmailConflict = httpsCallable<
                    { unitId: string; eventId: string; emails: string[] },
                    { email: string | null }
                >(functions, 'findEventRsvpEmailConflict');
                const { data: conflict } = await findEventRsvpEmailConflict({
                    unitId,
                    eventId,
                    emails: guests.map((guest) => guest.email),
                });
                if (conflict.email) {
                    emailConflict = { kind: 'already-on-event', email: conflict.email };
                }
            }
            if (emailConflict?.kind === 'duplicate-in-batch') {
                showError('Each guest email must be unique in this invitation.');
                throw new Error('Duplicate guest email in invitation batch');
            }
            if (emailConflict?.kind === 'already-on-event') {
                showError(`An RSVP or invitation already exists for ${emailConflict.email}.`);
                throw new Error(`Duplicate invitation email: ${emailConflict.email}`);
            }

            // Fetch unit information for the invitation
            const unitDoc = await getDoc(doc(db, 'units', unitId));
            const unitName = unitDoc.exists() ? unitDoc.data().name : 'Unknown Unit';

            // Generate invitation tokens and create pending RSVPs
            const invitationPromises = guests.map(async (guest) => {
                const invitationToken = crypto.randomUUID();
                const perGuestTicketFilter =
                    event?.tickets && event.tickets.length > 0 ? guest.inviteTicketId?.trim() : '';

                // Create pending RSVP (Firestore rejects undefined; omit optional fields)
                const rsvpData = {
                    eventId,
                    userId: '', // Will be filled when they accept
                    firstName: guest.firstName,
                    lastName: guest.lastName,
                    name: guest.name, // Keep for backward compatibility
                    email: guest.email.trim().toLowerCase(),
                    rank: '',
                    unit: '',
                    responses: {},
                    status: 'pending' as const,
                    invoiceId: rsvp?.invoiceId || '',
                    paid: false,
                    invitedBy: hostInviterIdentifier,
                    inviterPaysFor: guest.inviterPaysFor,
                    invitationToken,
                    checkedIn: false,
                    plusOneAllowed: false,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    ...(perGuestTicketFilter ? { ticketFilter: perGuestTicketFilter } : {}),
                };

                const docRef = await addDoc(
                    collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
                    rsvpData
                );

                // Check if the invitee already has an account and create in-app invitation
                // This is done via a Firebase Function to handle user lookup securely
                // Note: This may not work in local emulator due to CORS restrictions
                try {
                    const createInAppInvitation = httpsCallable(functions, 'createInAppInvitation');
                    
                    // Fetch venue data if venueId exists
                    let venueData = null;
                    if (event?.venueId && unitId) {
                        try {
                            const venueDoc = await getDoc(doc(db, 'units', unitId, 'venues', event.venueId));
                            if (venueDoc.exists()) {
                                venueData = venueDoc.data();
                            }
                        } catch (venueError) {
                            console.log('Could not fetch venue data:', venueError);
                        }
                    }
                    
                    await createInAppInvitation({
                        email: guest.email,
                        eventId,
                        eventName: event?.name || '',
                        eventDate: event?.date ? event.date.toISOString() : new Date().toISOString(),
                        eventLocation: event?.location || '',
                        eventVenue: venueData,
                        unitId,
                        unitName,
                        invitedBy: hostInviterIdentifier,
                        invitedByName: rsvp?.name || '',
                        inviterEmail: rsvp?.email || '',
                        message,
                        invitationToken,
                        inviterPaysFor: guest.inviterPaysFor,
                    });
                } catch (inviteError: any) {
                    // Silently handle errors - in-app invitations work in production
                    // CORS errors are expected in emulator; user may not have account
                    // Don't fail the whole invitation process if in-app notification fails
                }

                // Send email invitation via Firebase Function
                try {
                    const sendInvitationEmail = httpsCallable(functions, 'sendInvitationEmail');
                    // Build invitation link with ticket filter if the inviter's RSVP has one
                    const invitationLink = `${window.location.origin}/unit/${unitId}/event/${eventId}/invitation/${invitationToken}`;
                    // Note: The invitation page will redirect to RSVP page, so we'll handle ticket filter there
                    // For now, we store it in the RSVP data and use it when redirecting
                    
                    // Fetch venue data if venueId exists
                    let venueData = null;
                    if (event?.venueId && unitId) {
                        try {
                            const venueDoc = await getDoc(doc(db, 'units', unitId, 'venues', event.venueId));
                            if (venueDoc.exists()) {
                                venueData = venueDoc.data();
                            }
                        } catch (venueError) {
                            console.log('Could not fetch venue data:', venueError);
                        }
                    }
                    
                    await sendInvitationEmail({
                        to: guest.email,
                        guestName: guest.name,
                        inviterName: rsvp?.name || '',
                        eventName: event?.name || '',
                        eventDate: event?.date ? formatDate(event.date) : '',
                        eventLocation: event?.location || '',
                        eventVenue: venueData,
                        message,
                        invitationLink,
                    });
                } catch (emailError) {
                    console.error('Error sending invitation email:', emailError);
                    throw emailError;
                }

                return docRef.id;
            });

            await Promise.all(invitationPromises);

            await loadInvitedGuests(rsvp);
        } catch (error) {
            console.error('Error sending invites:', error);
            throw error;
        }
    };

    const handleLeaveWaitlist = async () => {
        if (!unitId || !eventId || !rsvpId) return;

        const result = await confirmAction({
            title: 'Leave waitlist?',
            text: 'You will be removed from the waitlist for this event.',
            confirmButtonText: 'Leave waitlist',
        });

        if (!result.isConfirmed) return;

        try {
            await applyRsvpCancellation();
            setRsvp(rsvp ? { ...rsvp, status: 'cancelled' } as RSVP : rsvp);
            showSuccess('You have left the waitlist.');
            navigate('/dashboard');
        } catch (error) {
            console.error('Error leaving waitlist:', error);
            showError('Failed to leave waitlist');
        }
    };

    const handleCancelRSVP = () => {
        setShowCancelModal(true);
    };

    const createCancellationNotification = async () => {
        if (!unitId || !eventId || !rsvp || !event) return;

        try {
            // Get unit members with admin/secretary permissions
            const unitDoc = await getDoc(doc(db, 'units', unitId));
            if (!unitDoc.exists()) return;

            const unitData = unitDoc.data();
            const members = unitData.members || [];
            
            // Filter members with admin or secretary roles
            const adminMembers = members.filter((member: any) => 
                member.role === 'admin' || member.role === 'secretary' || member.role === 'treasurer'
            );

            // Create notification for each admin member
            const notificationPromises = adminMembers.map(async (member: any) => {
                if (!member.userId) return;
                
                const notificationData = {
                    type: 'rsvp_cancelled',
                    title: 'RSVP Cancelled',
                    message: `${rsvp.name} has cancelled their RSVP for "${event.name}"${cancellationReason ? `: ${cancellationReason}` : ''}`,
                    eventId,
                    unitId,
                    rsvpId,
                    read: false,
                    createdAt: Timestamp.now(),
                };

                return addDoc(collection(db, 'users', member.userId, 'notifications'), notificationData);
            });

            await Promise.all(notificationPromises);
        } catch (error) {
            console.error('Error creating cancellation notification:', error);
        }
    };

    const sendCancellationEmails = async () => {
        if (!unitId || !eventId || !rsvp || !event) return;

        try {
            // Get unit members with admin/secretary permissions
            const unitDoc = await getDoc(doc(db, 'units', unitId));
            if (!unitDoc.exists()) return;

            const unitData = unitDoc.data();
            const members = unitData.members || [];
            
            // Filter members with admin or secretary roles
            const adminMembers = members.filter((member: any) => 
                (member.role === 'admin' || member.role === 'secretary' || member.role === 'treasurer') && member.email
            );

            // Send email to each admin member
            const emailPromises = adminMembers.map(async (member: any) => {
                const emailData = {
                    to: member.email,
                    subject: `RSVP Cancelled - ${event.name}`,
                    template: 'rsvp_cancelled',
                    data: {
                        memberName: member.name || member.firstName + ' ' + member.lastName,
                        eventName: event.name,
                        eventDate: event.date,
                        eventLocation: event.location,
                        rsvpName: rsvp.name,
                        cancellationReason: cancellationReason || 'No reason provided',
                        unitName: unitData.name,
                    }
                };

                // Call email function (assuming it exists)
                return fetch('/api/send-email', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(emailData)
                });
            });

            await Promise.all(emailPromises);
        } catch (error) {
            console.error('Error sending cancellation emails:', error);
        }
    };

    const handleSubmitCancellation = async () => {
        if (!unitId || !eventId || !rsvpId || !rsvp) return;

        setCancelling(true);
        try {
            // Update RSVP status to cancelled (guests route through the token-verified callable).
            await applyRsvpCancellation(cancellationReason);

            // Create notification for lodge users with permissions (best-effort; the onRsvpCancelled
            // Cloud Function performs the authoritative reconciliation server-side).
            await createCancellationNotification();

            // Send email notifications
            await sendCancellationEmails();

            setRsvp({ ...rsvp, status: 'cancelled' } as RSVP);
            showSuccess('Your RSVP has been cancelled. The lodge has been notified.');
            setShowCancelModal(false);
            setCancellationReason('');
            navigate('/dashboard');
        } catch (error) {
            console.error('Error cancelling RSVP:', error);
            showError('Failed to cancel RSVP. Please try again.');
        } finally {
            setCancelling(false);
        }
    };

    const handleCancelInvite = async (guestId: string, guestName: string) => {
        if (!unitId || !eventId) return;

        const result = await confirmAction({
            title: 'Cancel Invitation?',
            text: `Are you sure you want to cancel the invitation for ${guestName}? This cannot be undone.`,
            confirmButtonText: 'Yes, Cancel Invitation',
        });

        if (!result.isConfirmed) return;

        try {
            if (guestManageToken) {
                // Non-logged-in host: remove via the token-verified callable.
                const removeGuest = httpsCallable(functions, 'removeGuestFromBooking');
                const res = await removeGuest({
                    unitId,
                    eventId,
                    hostRsvpId: rsvpId,
                    guestRsvpId: guestId,
                    token: guestManageToken,
                });
                applyGuestMutationResult(res?.data);
            } else {
                // Delete the pending RSVP
                await deleteDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', guestId));
                setInvitedGuests(invitedGuests.filter(g => g.id !== guestId));
            }

            showSuccess(`Invitation for ${guestName} has been cancelled`);
        } catch (error) {
            console.error('Error canceling invitation:', error);
            showError('Failed to cancel invitation. Please try again.');
        }
    };

    const handleRemoveGuest = async (guest: RSVP) => {
        if (!unitId || !eventId) return;

        const inviterPays = isInviterPayingForGuest(guest);
        const guestCost = event ? ticketSubtotalForBreakdownRow(guest, event) : 0;

        let confirmText = `Are you sure you want to remove ${guest.name}? This cannot be undone.`;
        if (inviterPays && invoice && rsvp && event && guestCost > 0) {
            const remainingGuests = invitedGuests.filter((g) => g.id !== guest.id);
            const preview = computeHostInvoiceRecalc({
                event,
                payer: rsvp,
                allRsvps: [rsvp, ...remainingGuests],
                amountPaid: invoice.amountPaid || 0,
                pendingVerificationAmount: invoice.pendingVerificationAmount,
                previousStatus: invoice.status,
                transactionFee: invoice.transactionFee,
            });
            const currentAmountPaid = invoice.amountPaid || 0;
            const newBalance = preview.outstandingBalance;
            if (newBalance < 0 || preview.totalAmount < currentAmountPaid) {
                confirmText += ` Your invoice will be updated and you will have a credit of ${formatCurrency(Math.abs(newBalance))}.`;
            } else {
                confirmText += ' Their cost will be removed from your invoice.';
            }
        }

        const result = await confirmAction({
            title: 'Remove Guest?',
            text: confirmText,
            confirmButtonText: 'Yes, Remove Guest',
        });

        if (!result.isConfirmed) return;

        setRemovingGuestId(guest.id);
        try {
            // Non-logged-in guest hosts remove via the token-verified callable (which deletes the guest,
            // fixes the attendee count and recomputes the host invoice server-side).
            if (guestManageToken) {
                const removeGuest = httpsCallable(functions, 'removeGuestFromBooking');
                const res = await removeGuest({
                    unitId,
                    eventId,
                    hostRsvpId: rsvpId,
                    guestRsvpId: guest.id,
                    token: guestManageToken,
                });
                applyGuestMutationResult(res?.data);
                showSuccess(`${guest.name} has been removed`);
                return;
            }

            if (inviterPays && invoice && rsvp && event) {
                const remainingGuests = invitedGuests.filter((g) => g.id !== guest.id);
                const recalc = await persistHostInvoiceRecalc({
                    unitId,
                    eventId,
                    invoice,
                    event,
                    payer: rsvp,
                    allRsvps: [rsvp, ...remainingGuests],
                });
                setInvoice({
                    ...invoice,
                    totalAmount: recalc.totalAmount,
                    subtotal: recalc.subtotal,
                    outstandingBalance: recalc.outstandingBalance,
                    status: recalc.status,
                    rsvps: recalc.rsvpIds,
                    accountingTotals: recalc.accountingTotals,
                    ...(recalc.allInPriceBreakdown
                        ? { allInPriceBreakdown: recalc.allInPriceBreakdown }
                        : {}),
                });
            }

            // Remove guest via server-side safe delete (email index, attendee count, soft/hard delete)
            const deleteRsvpSafe = httpsCallable(functions, 'deleteRsvpSafe');
            await deleteRsvpSafe({
                unitId,
                eventId,
                rsvpId: guest.id,
            });

            // Update local state
            setInvitedGuests(invitedGuests.filter(g => g.id !== guest.id));

            showSuccess(`${guest.name} has been removed`);
        } catch (error) {
            console.error('Error removing guest:', error);
            showError('Failed to remove guest. Please try again.');
        } finally {
            setRemovingGuestId(null);
        }
    };

    const handleResendInvite = async (guest: RSVP) => {
        if (!unitId || !eventId || !rsvp) return;
        if (!currentUser) {
            showError('Please log in to manage additional guests.');
            return;
        }
        if (!guest.email?.trim()) {
            showError('This invitation does not have an email address.');
            return;
        }
        if (!guest.invitationToken?.trim()) {
            showError('This invitation cannot be resent because its token is missing.');
            return;
        }

        setResendingInviteGuestId(guest.id);
        try {
            let venueData = null;
            if (event?.venueId) {
                try {
                    const venueDoc = await getDoc(doc(db, 'units', unitId, 'venues', event.venueId));
                    if (venueDoc.exists()) {
                        venueData = venueDoc.data();
                    }
                } catch (venueError) {
                    console.log('Could not fetch venue data:', venueError);
                }
            }

            const invitationLink =
                `${window.location.origin}/unit/${unitId}/event/${eventId}/invitation/${guest.invitationToken}`;
            const sendInvitationEmail = httpsCallable(functions, 'sendInvitationEmail');

            await sendInvitationEmail({
                to: guest.email,
                guestName: guest.name || `${guest.firstName || ''} ${guest.lastName || ''}`.trim() || 'Guest',
                inviterName: rsvp.name || '',
                eventName: event?.name || '',
                eventDate: event?.date ? formatDate(event.date) : '',
                eventLocation: event?.location || '',
                eventVenue: venueData,
                message: `You're invited to ${event?.name || 'this event'}! Please click the link below to RSVP.`,
                invitationLink,
            });

            showSuccess(`Invitation resent to ${guest.email}`);
        } catch (error) {
            console.error('Error resending invitation email:', error);
            showError('Failed to resend invitation email. Please try again.');
        } finally {
            setResendingInviteGuestId(null);
        }
    };

    const handleShowCheckInQRCode = async (guest?: RSVP) => {
        if (!unitId || !eventId) {
            showError('Invalid event information');
            return;
        }

        try {
            let qrData: string;
            let displayName: string;

            if (guest) {
                // Generate QR code for specific guest's RSVP
                qrData = JSON.stringify({
                    unitId,
                    eventId,
                    rsvpId: guest.id,
                    name: guest.name,
                });
                displayName = guest.name;
                setQrCodeForGuest(guest);
            } else {
                // Generate QR code for check-in page (organizer view)
                qrData = `${window.location.origin}/unit/${unitId}/event/${eventId}/check-in`;
                displayName = event?.name || 'Event';
                setQrCodeForGuest(null);
            }
            
            const qrUrl = await QRCode.toDataURL(qrData, {
                width: 300,
                margin: 2,
                color: {
                    dark: '#1E3A5F',
                    light: '#FFFFFF',
                },
            });
            
            setQrCodeUrl(qrUrl);
            setShowQRCodeDialog(true);
        } catch (error) {
            console.error('Error generating QR code:', error);
            showError('Failed to generate QR code');
        }
    };

    const handleAddToAppleWallet = async () => {
        if (!unitId || !eventId || !event || !rsvp) {
            showError('Missing event information');
            return;
        }

        try {
            // Create a basic Apple Wallet pass structure
            // Note: For production, this should be generated server-side with proper signing
            const passData = {
                formatVersion: 1,
                passTypeIdentifier: 'pass.com.masonictools.event',
                serialNumber: rsvp.id,
                teamIdentifier: 'TEAM_ID', // Would need actual team ID
                organizationName: currentUnit?.name || 'Masonic Tools',
                description: `Check-in pass for ${event.name}`,
                logoText: event.name,
                foregroundColor: 'rgb(30, 58, 95)',
                backgroundColor: 'rgb(255, 255, 255)',
                eventTicket: {
                    primaryFields: [
                        {
                            key: 'event',
                            label: 'Event',
                            value: event.name,
                        },
                    ],
                    secondaryFields: [
                        {
                            key: 'date',
                            label: 'Date',
                            value: formatDate(event.date),
                        },
                        {
                            key: 'name',
                            label: 'Name',
                            value: rsvp.name,
                        },
                    ],
                    auxiliaryFields: [
                        {
                            key: 'location',
                            label: 'Location',
                            value: event.location || 'TBA',
                        },
                    ],
                    barcode: {
                        message: JSON.stringify({
                            unitId,
                            eventId,
                            rsvpId: rsvp.id,
                            name: rsvp.name,
                        }),
                        format: 'PKBarcodeFormatQR',
                        messageEncoding: 'iso-8859-1',
                    },
                },
            };

            // Convert QR code to base64 for embedding
            const qrCodeBase64 = qrCodeUrl.split(',')[1];

            // Create a simple pass file (basic structure)
            // Note: This is a simplified version. Full implementation requires:
            // 1. Server-side generation with proper certificates
            // 2. Signing with Apple Developer certificates
            // 3. Creating a proper .pkpass file (ZIP archive)
            
            showError('Apple Wallet integration requires server-side pass generation with certificates. This feature will be available soon.');
            
            // For now, provide download of QR code as alternative
            // In production, this would call a backend function to generate signed .pkpass file
        } catch (error) {
            console.error('Error creating Apple Wallet pass:', error);
            showError('Failed to create Apple Wallet pass');
        }
    };

    const handleAddToGoogleWallet = async () => {
        if (!unitId || !eventId || !event || !rsvp) {
            showError('Missing event information');
            return;
        }

        try {
            // Google Wallet requires server-side API integration
            // For now, we'll provide a link to the check-in page
            // In production, this would:
            // 1. Call backend function to create Google Wallet pass
            // 2. Return a signed JWT
            // 3. Redirect to https://pay.google.com/gp/v/save/<jwt>
            
            showError('Google Wallet integration requires Google Wallet API setup. This feature will be available soon.');
            
            // Alternative: Open check-in page in new tab
            const checkInUrl = `${window.location.origin}/unit/${unitId}/event/${eventId}/check-in`;
            window.open(checkInUrl, '_blank');
        } catch (error) {
            console.error('Error creating Google Wallet pass:', error);
            showError('Failed to create Google Wallet pass');
        }
    };

    const handleEditGuestMealChoices = (guestId: string) => {
        const guest = invitedGuests.find(g => g.id === guestId);
        if (!guest || !event) return;

        // Check if using ticket system
        if (event.tickets && event.tickets.length > 0 && guest.responses?.Tickets && Array.isArray(guest.responses.Tickets)) {
            const ticket = (guest.responses.Tickets as any[])[0]; // Get first ticket (single guest)
            if (ticket) {
                setEditingGuestTicketId(ticket.id || '');
                // Load course selections for this ticket
                if (guest.responses.TicketCourses && guest.responses.TicketCourses[ticket.id]) {
                    const normalized = normalizeTicketDataToFormatA(
                      guest.responses.TicketCourses[ticket.id]
                    );
                    const instanceSelections =
                      normalized?.[DEFAULT_INSTANCE_INDEX] ?? {};
                    setEditingGuestTicketCourses(instanceSelections);
                } else {
                    setEditingGuestTicketCourses({});
                }
            } else {
                setEditingGuestTicketId('');
                setEditingGuestTicketCourses({});
            }
        } else {
            // Legacy course/meal system
        const currentChoices: Record<string, string> = {};
        if (guest.responses) {
            Object.entries(guest.responses).forEach(([key, value]) => {
                    if (key !== 'Allergies' && key !== 'Tickets' && key !== 'TicketCourses') {
                    currentChoices[key] = String(value);
                }
            });
        }
        setEditingMealChoices(currentChoices);
        }
        // Initialize payment option - explicitly check if inviterPaysFor is set
        // If it's explicitly false, use false; otherwise default to true
        const inviterPaysFor = guest.inviterPaysFor !== undefined ? guest.inviterPaysFor : true;
        setEditingGuestInviterPaysFor(inviterPaysFor);
        setEditingGuest(guestId);
    };

    const handleSaveGuestMealChoices = async (guestId: string) => {
        if (!unitId || !eventId || !event) return;

        try {
            const guestRef = doc(db, 'units', unitId, 'events', eventId, 'rsvps', guestId);
            const guest = invitedGuests.find(g => g.id === guestId);
            if (!guest) return;

            // Non-logged-in host: edit via the token-verified callable (server rebuilds the ticket/meal
            // responses from event config and recomputes the host invoice).
            if (guestManageToken) {
                if (event.tickets && event.tickets.length > 0) {
                    if (!editingGuestTicketId) {
                        showError('Please select a ticket type');
                        return;
                    }
                    if (!editGuestTicketOptions.some((t) => t.id === editingGuestTicketId)) {
                        showError('Invalid ticket type selected');
                        return;
                    }
                    const selectedTicketType = event.tickets.find((t) => t.id === editingGuestTicketId);
                    if (selectedTicketType?.courses?.length) {
                        for (const course of selectedTicketType.courses) {
                            if (!editingGuestTicketCourses[course.id]) {
                                showError(`Please select an option for ${course.name}`);
                                return;
                            }
                        }
                    }
                }
                const editGuest = httpsCallable(functions, 'editGuestInBooking');
                const res = await editGuest({
                    unitId,
                    eventId,
                    hostRsvpId: rsvpId,
                    guestRsvpId: guestId,
                    token: guestManageToken,
                    inviterPaysFor: editingGuestInviterPaysFor,
                    guest: {
                        ticketTypeId: editingGuestTicketId || '',
                        ticketCourseSelections: editingGuestTicketCourses,
                        courseSelections: editingMealChoices,
                        allergies: guest.responses?.Allergies || '',
                    },
                });
                applyGuestMutationResult(res?.data);
                setEditingGuest(null);
                setEditingMealChoices({});
                setEditingGuestTicketId('');
                setEditingGuestTicketCourses({});
                setEditingGuestInviterPaysFor(true);
                showSuccess('Guest meal choices updated successfully!');
                return;
            }

            // Create a fresh responses object, preserving only non-ticket fields
            let updatedResponses: any = {
                Allergies: guest.responses?.Allergies || '',
            };
            
            // Preserve other non-ticket response fields (excluding Tickets and TicketCourses which we'll update)
            if (guest.responses) {
                Object.entries(guest.responses).forEach(([key, value]) => {
                    if (key !== 'Tickets' && key !== 'TicketCourses' && key !== 'Allergies') {
                        updatedResponses[key] = value;
                    }
                });
            }

            // Check if using ticket system
            if (event.tickets && event.tickets.length > 0) {
                if (!editingGuestTicketId) {
                    showError('Please select a ticket type');
                    return;
                }

                if (!editGuestTicketOptions.some((t) => t.id === editingGuestTicketId)) {
                    showError('Invalid ticket type selected');
                    return;
                }

                const selectedTicketType = event.tickets.find(t => t.id === editingGuestTicketId);
                if (!selectedTicketType) {
                    showError('Invalid ticket type selected');
                    return;
                }

                // Validate all courses are selected if ticket has courses
                if (selectedTicketType.courses && selectedTicketType.courses.length > 0) {
                    for (const course of selectedTicketType.courses) {
                        if (!editingGuestTicketCourses[course.id]) {
                            showError(`Please select an option for ${course.name}`);
                            return;
                        }
                    }
                }

                // Update ticket - create new array with new object
                const ticket = (guest.responses?.Tickets as any[])?.[0] || null;
                updatedResponses.Tickets = [{
                    id: selectedTicketType.id,
                    name: selectedTicketType.name,
                    price: ticket?.price || selectedTicketType.price,
                    qty: 1,
                }];

                // Update ticket courses
                if (selectedTicketType.courses && selectedTicketType.courses.length > 0) {
                    const existingNormalized = normalizeTicketCoursesToFormatA(
                      guest.responses?.TicketCourses
                    );
                    const newTicketCourses: any = {};
                    Object.entries(existingNormalized).forEach(([tid, courses]) => {
                        if (tid !== selectedTicketType.id) {
                            newTicketCourses[tid] = courses;
                        }
                    });
                    newTicketCourses[selectedTicketType.id] = {
                        [DEFAULT_INSTANCE_INDEX]: { ...editingGuestTicketCourses },
                    };
                    updatedResponses.TicketCourses = newTicketCourses;
                } else {
                    const existingNormalized = normalizeTicketCoursesToFormatA(
                      guest.responses?.TicketCourses
                    );
                    const newTicketCourses: any = {};
                    Object.entries(existingNormalized).forEach(([tid, courses]) => {
                        if (tid !== selectedTicketType.id) {
                            newTicketCourses[tid] = courses;
                        }
                    });
                    updatedResponses.TicketCourses =
                      Object.keys(newTicketCourses).length > 0 ? newTicketCourses : null;
                }
            } else {
                // Legacy course/meal system
                updatedResponses = {
                ...editingMealChoices,
                    Allergies: guest.responses?.Allergies || '',
                };
            }

            // Clean undefined values
            Object.keys(updatedResponses).forEach(key => {
                if (updatedResponses[key] === undefined) {
                    delete updatedResponses[key];
                }
            });

            const updatedGuest: RSVP = {
                ...guest,
                responses: { ...updatedResponses },
                inviterPaysFor: editingGuestInviterPaysFor,
                updatedAt: new Date(),
                ...(editingGuestInviterPaysFor && invoice
                    ? { invoiceId: invoice.id }
                    : { invoiceId: '', paid: false }),
            };

            await updateDoc(guestRef, {
                responses: updatedResponses,
                inviterPaysFor: editingGuestInviterPaysFor,
                updatedAt: Timestamp.now(),
                ...(editingGuestInviterPaysFor && invoice
                    ? { invoiceId: invoice.id }
                    : { invoiceId: '', paid: false }),
            });

            if (invoice && rsvp) {
                const allRsvpsForInvoice = [
                    rsvp,
                    ...invitedGuests.map((g) => (g.id === guestId ? updatedGuest : g)),
                ];
                const recalc = await persistHostInvoiceRecalc({
                    unitId,
                    eventId,
                    invoice,
                    event,
                    payer: rsvp,
                    allRsvps: allRsvpsForInvoice,
                });
                setInvoice({
                    ...invoice,
                    totalAmount: recalc.totalAmount,
                    subtotal: recalc.subtotal,
                    outstandingBalance: recalc.outstandingBalance,
                    status: recalc.status,
                    rsvps: recalc.rsvpIds,
                    accountingTotals: recalc.accountingTotals,
                    ...(recalc.allInPriceBreakdown
                        ? { allInPriceBreakdown: recalc.allInPriceBreakdown }
                        : {}),
                });
            }

            // Update local state - create completely new object references to ensure React detects the change
            setInvitedGuests(prevGuests => prevGuests.map(g => {
                if (g.id === guestId) {
                    return updatedGuest;
                }
                return g;
            }));

            setEditingGuest(null);
            setEditingMealChoices({});
            setEditingGuestTicketId('');
            setEditingGuestTicketCourses({});
            setEditingGuestInviterPaysFor(true);
            showSuccess('Guest meal choices updated successfully!');
        } catch (error) {
            console.error('Error updating guest meal choices:', error);
            showError('Failed to update meal choices. Please try again.');
        }
    };

    const handleCancelEditMealChoices = () => {
        setEditingGuest(null);
        setEditingMealChoices({});
        setEditingGuestTicketId('');
        setEditingGuestTicketCourses({});
        setEditingGuestInviterPaysFor(true);
    };

    const rsvpTicketSubtotal = (rsvpData: RSVP | null): number =>
        rsvpData && event ? ticketSubtotalForBreakdownRow(rsvpData, event) : 0;

    const isInviterPayingForGuest = (guest: RSVP): boolean => guest.inviterPaysFor !== false;

    /** True when this RSVP is an invitee whose host pays for their ticket (nothing for them to pay for themselves). */
    const invitedGuestInviterCoversMe =
        !!rsvp &&
        !rsvp.apologies &&
        typeof rsvp.invitedBy === 'string' &&
        rsvp.invitedBy.trim() !== '' &&
        isInviterPayingForGuest(rsvp);

    const handlePayNow = async () => {
        // Check for required parameters
        if (!unitId || !eventId || !rsvpId) {
            showError('Missing required parameters. Please refresh the page and try again.');
            return;
        }

        // Check if event is loaded
        if (!event) {
            showError('Event information is not available. Please refresh the page and try again.');
            return;
        }

        // Check if RSVP is loaded
        if (!rsvp) {
            showError('RSVP information is not available. Please refresh the page and try again.');
            return;
        }

        // Guests can't write invoices (tightened rules) and don't need to: their invoice was created
        // with correct totals at RSVP time. Go straight to the payment page, which resolves the
        // invoice server-side via getInvoiceForPayment.
        if (!currentUser) {
            if (invoice?.id) {
                navigate(`/payment/${invoice.id}`);
            } else {
                showError('We could not find your invoice. Please use the payment link from your confirmation email.');
            }
            return;
        }

        try {
            // Fetch system settings and use bank transfer fee for invoice totals.
            // Online method fees are applied at checkout in PaymentPage.
            const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
            const transactionFee = settingsDoc.exists()
                ? (() => {
                    const data = settingsDoc.data() as {
                        paymentMethods?: {
                            bankTransfer?: {
                                transactionFee?: number;
                            };
                        };
                    };

                    return data.paymentMethods?.bankTransfer?.transactionFee ?? 0;
                })()
                : 0;
            const platformBookingFeePence = getCreditBookingFeePenceForEvent(event);

            const mainRsvpCost =
                invitedGuestInviterCoversMe ? 0 : rsvpTicketSubtotal(rsvp);
            const guestsInviterPaysForPayNow = invitedGuests.filter(
                (g) => isInviterPayingForGuest(g) && !g.apologies,
            );
            const guestsCost = guestsInviterPaysForPayNow.reduce(
                (sum, guest) => sum + rsvpTicketSubtotal(guest),
                0,
            );
            const baseAmount = mainRsvpCost + guestsCost;

            const rsvpIds = [rsvpId, ...guestsInviterPaysForPayNow.map((g) => g.id)];
            const billableBookingSlots =
                (invitedGuestInviterCoversMe ? 0 : ticketSubtotalForBreakdownRow(rsvp, event) > 0 ? 1 : 0) +
                guestsInviterPaysForPayNow.filter((g) => ticketSubtotalForBreakdownRow(g, event) > 0).length;
            const creditRecoveryTotal =
                baseAmount > 0
                    ? computeCreditRecoveryTotalPounds(
                          event,
                          baseAmount,
                          billableBookingSlots,
                          platformBookingFeePence,
                      )
                    : 0;
            const totalWithRecovery = baseAmount + creditRecoveryTotal;

            const invoiceCalculation = calculateInvoiceAmounts(totalWithRecovery, transactionFee);

            // Check if invoice already exists
            let invoiceRef;
            let invoiceId: string;
            let invoiceNumber: string;

            if (invoice) {
                // Reuse host invoice even when previously paid (e.g. new guests added later)
                invoiceRef = doc(db, 'units', unitId, 'events', eventId, 'invoices', invoice.id);
                invoiceId = invoice.id;
                invoiceNumber = invoice.invoiceNumber || generateInvoiceNumber();

                const recalc = computeHostInvoiceRecalc({
                    event,
                    payer: rsvp,
                    allRsvps: [rsvp, ...guestsInviterPaysForPayNow],
                    amountPaid: invoice.amountPaid || 0,
                    pendingVerificationAmount: invoice.pendingVerificationAmount,
                    previousStatus: invoice.status,
                    transactionFee,
                });

                await updateDoc(invoiceRef, {
                    totalAmount: recalc.totalAmount,
                    subtotal: recalc.subtotal,
                    vatAmount: 0,
                    vatRate: 0,
                    ...(recalc.transactionFee !== undefined ? { transactionFee: recalc.transactionFee } : {}),
                    outstandingBalance: recalc.outstandingBalance,
                    status: recalc.status,
                    rsvps: recalc.rsvpIds,
                    accountingTotals: recalc.accountingTotals,
                    ...(recalc.allInPriceBreakdown
                        ? { allInPriceBreakdown: recalc.allInPriceBreakdown }
                        : {}),
                    invoiceNumber,
                    updatedAt: Timestamp.now(),
                });

                showSuccess('Invoice updated! Redirecting to payment...');
            } else {
                // Create new invoice with unit-based reference (e.g. 5461-45)
                invoiceNumber = await generateUnitInvoiceNumber(db, unitId);
                // Firestore rules allow unauthenticated invoice create only when ownerUid is empty.
                // Do not copy rsvp.userId here when logged out: that would set ownerUid and make addDoc permission-denied.
                // RSVP linkage is still stored on the invoice via rsvps[].
                const ownerUid = currentUser?.uid ?? '';
                const newInvoiceData = {
                    eventId,
                    unitId,
                    ownerUid: ownerUid,
                    totalAmount: invoiceCalculation.totalAmount,
                    subtotal: invoiceCalculation.subtotal,
                    vatAmount: 0, // VAT is already included in ticket price
                    vatRate: 0,
                    ...(transactionFee > 0 ? { transactionFee } : {}),
                    amountPaid: 0,
                    outstandingBalance: invoiceCalculation.totalAmount,
                    status: 'outstanding' as const,
                    rsvps: rsvpIds,
                    paymentHistory: [],
                    paymentLink: '', // Will be set after creation
                    remindersSent: [],
                    invoiceNumber: invoiceNumber,
                    accountingTotals: {
                        totalLodgeTransfer: baseAmount,
                        totalPlatformProfit: 0,
                        platformFeeTotal: creditRecoveryTotal,
                    },
                    ...(creditRecoveryTotal > 0
                        ? {
                              allInPriceBreakdown: {
                                  ticketPrice: baseAmount,
                                  creditCostRecovery: creditRecoveryTotal,
                              },
                          }
                        : {}),
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                };

                invoiceRef = await addDoc(
                    collection(db, 'units', unitId, 'events', eventId, 'invoices'),
                    newInvoiceData
                );
                invoiceId = invoiceRef.id;

                // Update payment link
                const paymentLink = buildInvoicePaymentLink(window.location.origin, invoiceId);
                await updateDoc(invoiceRef, { paymentLink });

                // Update RSVPs with invoice ID
                for (const id of rsvpIds) {
                    await updateDoc(
                        doc(db, 'units', unitId, 'events', eventId, 'rsvps', id),
                        { invoiceId: invoiceId }
                    );
                }

                showSuccess('Invoice created! Redirecting to payment...');
            }

            // Navigate to payment page
            navigate(`/payment/${invoiceId}`);
        } catch (error: unknown) {
            console.error('Error creating/updating invoice:', error);
            const code =
                error && typeof error === 'object' && 'code' in error
                    ? String((error as { code?: string }).code)
                    : '';
            if (code === 'permission-denied') {
                showError(
                    currentUser
                        ? 'Could not update this invoice. You may not have permission, or the event may no longer accept payments. Contact the organiser if this continues.'
                        : 'Could not create invoice. Log in with the account used to RSVP, or ask the organiser if the event has closed for payments.',
                );
            } else if (code === 'unavailable' || code === 'failed-precondition') {
                showError(
                    'Could not reach the server to create your invoice. Check your connection and try again.',
                );
            } else {
                showError('Failed to create invoice. Please try again.');
            }
        }
    };

    const handleAddManualGuest = async () => {
        if (!unitId || !eventId || !rsvpId || !event) return;
        if (addingGuest) return;

        // Validate required fields
        if (!newGuestForm.firstName || !newGuestForm.lastName) {
            showError('Please fill in all required fields (First Name and Last Name)');
            return;
        }

        // Validate ticket and course selections
        if (event?.tickets && event.tickets.length > 0) {
            if (!newGuestForm.ticketTypeId) {
                showError('Please select a ticket type');
                return;
            }
            if (!inviterAccessibleTickets.some((t) => t.id === newGuestForm.ticketTypeId)) {
                showError('Please select a ticket you are allowed to invite with.');
                return;
            }
            const selectedTicket = event.tickets.find(t => t.id === newGuestForm.ticketTypeId);
            if (selectedTicket?.courses && selectedTicket.courses.length > 0) {
                const missingSelections = selectedTicket.courses.filter(course => !newGuestForm.ticketCourseSelections[course.id]);
                if (missingSelections.length > 0) {
                    showError('Please select an option for all courses');
                    return;
                }
            }
        } else if (event?.courses && event.courses.length > 0) {
            const missingSelections = event.courses.filter(course => !newGuestForm.courseSelections[course.id]);
            if (missingSelections.length > 0) {
                showError('Please select an option for all courses');
                return;
            }
        }

        setAddingGuest(true);
        const fullName = `${newGuestForm.firstName} ${newGuestForm.lastName}`.trim();
        try {
            // Compute the rank short name client-side (from unit-type/custom prefixes, then the
            // legacy global ranks); everything else — ticket pricing and, for inviter-pays, the
            // host-invoice recompute — is done server-side via addGuestToBooking. This makes the
            // flow work for non-logged-in guest hosts, who can no longer write the host invoice
            // directly (that was the V3 IDOR vector).
            let rankShortName: string | null = null;
            if (newGuestForm.rank) {
                if (event?.useCustomPrefixes) {
                    const matchedPrefix = prefixSelectOptions.find((p) => p.name === newGuestForm.rank);
                    rankShortName = matchedPrefix?.shortName?.trim() || null;
                } else {
                    const resolved = getRankAndShortName({
                        rankName: newGuestForm.rank,
                        currentUnitType: currentUnitTypeForPrefixes,
                        globalRanks: ranks,
                    });
                    rankShortName = resolved.rankShortName;
                }
            }

            const addGuest = httpsCallable(functions, 'addGuestToBooking');
            const result = await addGuest({
                unitId,
                eventId,
                hostRsvpId: rsvpId,
                token: guestManageToken ?? '',
                inviterPaysFor: newGuestForm.inviterPaysFor,
                guest: {
                    firstName: newGuestForm.firstName,
                    lastName: newGuestForm.lastName,
                    rank: newGuestForm.rank || null,
                    rankShortName,
                    additionalRank: newGuestForm.additionalRank?.trim() || null,
                    unit: newGuestForm.unit?.trim() || null,
                    email: newGuestForm.email?.trim() || null,
                    allergies: newGuestForm.allergies,
                    ticketTypeId: newGuestForm.ticketTypeId?.trim() || '',
                    ticketCourseSelections: newGuestForm.ticketCourseSelections,
                    courseSelections: newGuestForm.courseSelections,
                },
            });

            // Reflect the server-recomputed host invoice (inviter-pays case) and the refreshed guest
            // list (the rsvps collection isn't guest-listable) in local state.
            const resultData = result?.data as {
                invoice?: {
                    totalAmount: number;
                    outstandingBalance: number;
                    status: Invoice['status'];
                    rsvps: string[];
                } | null;
                invitedGuests?: RSVP[];
            } | undefined;
            const updatedInvoice = resultData?.invoice;
            if (updatedInvoice && invoice) {
                setInvoice({
                    ...invoice,
                    totalAmount: updatedInvoice.totalAmount,
                    outstandingBalance: updatedInvoice.outstandingBalance,
                    status: updatedInvoice.status,
                    rsvps: updatedInvoice.rsvps,
                });
            }
            if (Array.isArray(resultData?.invitedGuests)) {
                setInvitedGuests(resultData.invitedGuests);
            }
            await loadInvitedGuests(rsvp);

            // Reset form
            setNewGuestForm({
                firstName: '',
                lastName: '',
                rank: '',
                additionalRank: '',
                unit: '',
                email: '',
                allergies: '',
                courseSelections: {},
                ticketTypeId: '',
                ticketPrice: 0,
                ticketCourseSelections: {},
                inviterPaysFor: true,
            });
            setShowAddGuestModal(false);

            showSuccess(`${fullName} has been added as a guest!`);
        } catch (error) {
            console.error('Error adding manual guest:', error);
            showError('Failed to add guest. Please try again.');
        } finally {
            setAddingGuest(false);
        }
    };

    // Calculate payment summary based on actual attendees
    const attendingGuests = [rsvp, ...invitedGuests].filter((r) => !r?.apologies);
    const totalGuests = attendingGuests.length;
    const paidGuests = attendingGuests.filter((r) => r?.paid).length;
    
    // Calculate total amount: main RSVP + guests where inviter is paying
    // Always calculate live from current event ticket prices, not from saved invoice data
    const mainRsvpTicketPrice = rsvpTicketSubtotal(rsvp);
    const mainRsvpCost = invitedGuestInviterCoversMe ? 0 : mainRsvpTicketPrice;
    const guestsInviterPaysFor = invitedGuests.filter((g) => isInviterPayingForGuest(g) && !g.apologies);
    const guestsPayingSeparately = invitedGuests.filter((g) => !isInviterPayingForGuest(g) && !g.apologies);
    const guestsCost = guestsInviterPaysFor.reduce((sum, guest) => sum + rsvpTicketSubtotal(guest), 0);
    const ticketSubtotalOnly = mainRsvpCost + guestsCost;
    const skipInvoiceBookingFees = invitedGuestInviterCoversMe && guestsCost === 0;
    const billableRsvpCountForBookingFee = skipInvoiceBookingFees
        ? 0
        : (mainRsvpCost > 0 ? 1 : 0) +
          guestsInviterPaysFor.filter((g) => (event ? ticketSubtotalForBreakdownRow(g, event) : 0) > 0).length;
    const bookingFeeTotal =
        event && !skipInvoiceBookingFees
            ? computeCreditRecoveryTotalPounds(
                  event,
                  ticketSubtotalOnly,
                  billableRsvpCountForBookingFee,
                  creditBookingFeePence,
              )
            : 0;
    const perSlotOnHostBill = getBookingFeePerBillableSlotPounds(
        event,
        ticketSubtotalOnly,
        billableRsvpCountForBookingFee,
        creditBookingFeePence,
    );
    const calculatedTotalAmount = ticketSubtotalOnly + bookingFeeTotal;
    
    // Always use live-calculated total for display (not invoice data which may be stale)
    // This ensures the display updates immediately when ticket prices change
    const displayTotal = calculatedTotalAmount;
    const totalAmount = invoice?.totalAmount || calculatedTotalAmount; // Keep for invoice calculations
    // Do not use the host's invoice totals for an invitee's "your balance" when the host pays for their ticket
    const useInvoiceForMyPaymentDisplay =
        !invitedGuestInviterCoversMe || guestsCost > 0;
    const amountPaid = useInvoiceForMyPaymentDisplay
        ? invoice
            ? getEffectiveAmountPaid(invoice)
            : 0
        : 0;
    const amountReserved = useInvoiceForMyPaymentDisplay
        ? invoice
            ? getAmountReservedOnInvoice(invoice)
            : 0
        : 0;

    // Calculate outstanding balance based on display total (tickets plus booking fee where applicable)
    // A negative value indicates a credit (user has overpaid due to guest removal)
    const outstandingBalance = useInvoiceForMyPaymentDisplay
        ? displayTotal - amountReserved
        : 0;

    // Track who has paid (RSVP flag can desync from the invoice; badge must match money owed)
    const mainRsvpPaid = rsvp?.paid || false;
    const showMainLinePaid =
        mainRsvpPaid &&
        (mainRsvpCost === 0 || !useInvoiceForMyPaymentDisplay || outstandingBalance <= 0);
    const paidGuestsCount = invitedGuests.filter(g => g.paid && !g.apologies).length;
    const unpaidGuestsInviterPaysFor = guestsInviterPaysFor.filter(g => !g.paid);

    if (loading) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-center">
                        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                        <p className="text-muted-foreground">Loading your RSVP...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show identity verification form for non-logged-in users (check before rsvp/event validation)
    // This allows verification even if event hasn't loaded yet
    if (needsVerification) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <main className="flex-1 py-8">
                    <DashboardContentContainer>
                    <div className="w-full sm:mx-auto sm:max-w-2xl space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Verify Your Identity</CardTitle>
                                <CardDescription>
                                    Please confirm your details to access your RSVP dashboard
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleVerifyIdentity} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="verify-name">
                                            Full Name <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            id="verify-name"
                                            value={verificationData.name}
                                            onChange={(e) => setVerificationData({ ...verificationData, name: e.target.value })}
                                            placeholder="Enter your full name"
                                            required
                                        />
                                    </div>

                                    {verificationRequiresPrefix && (
                                        <div className="space-y-2">
                                            <Label htmlFor="verify-rank">
                                                Prefix <span className="text-red-500">*</span>
                                            </Label>
                                            <Select
                                                value={verificationData.rank}
                                                onValueChange={(value) => setVerificationData({ ...verificationData, rank: value })}
                                                required
                                            >
                                                <SelectTrigger id="verify-rank">
                                                    <SelectValue placeholder="Select your prefix" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {prefixSelectOptions.map((prefix) => (
                                                        <SelectItem key={prefix.id} value={prefix.name}>
                                                            {prefix.shortName
                                                                ? `${prefix.name} (${prefix.shortName})`
                                                                : prefix.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {verificationRequiresPrefix &&
                                                currentUnit &&
                                                currentUnitTypeForPrefixes &&
                                                !hasConfiguredUnitTypePrefixes && (
                                                    <p className="text-xs text-amber-600">
                                                        No prefixes are configured for this unit type. Please contact the
                                                        event organiser.
                                                    </p>
                                                )}
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="verify-email">
                                            Email Address <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            id="verify-email"
                                            type="email"
                                            value={verificationData.email}
                                            onChange={(e) => setVerificationData({ ...verificationData, email: e.target.value })}
                                            placeholder="Enter your email address"
                                            required
                                        />
                                    </div>

                                    <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                                        <p className="text-sm text-blue-900 mb-2">
                                            <strong>Why do we need this?</strong>
                                        </p>
                                        <p className="text-sm text-blue-800">
                                            To protect your RSVP information, we need to verify that you are the person who registered for this event.
                                        </p>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={verifying}
                                    >
                                        {verifying ? 'Verifying...' : 'Verify & Continue'}
                                    </Button>
                                </form>

                                <div className="pt-4 border-t">
                                    <Card className="bg-muted">
                                        <CardContent className="py-4 space-y-3">
                                            <p className="text-sm font-semibold">Need easier access?</p>
                                            <p className="text-sm text-muted-foreground">
                                                Create a free account to access your RSVP dashboard anytime without verification.
                                            </p>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate('/signup')}
                                                >
                                                    Create Account
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => navigate('/login')}
                                                >
                                                    Already have an account? Login
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    </DashboardContentContainer>
                </main>
            </div>
        );
    }

    // Validate that RSVP and event exist (after verification check)
    if (!rsvp || !event) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-center">
                        <p className="text-muted-foreground">RSVP not found</p>
                        <Button className="mt-4" onClick={() => navigate('/dashboard')}>
                            Go to Dashboard
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <EventPageBackground
            backgroundImageUrl={event?.backgroundImageUrl}
            backgroundColor={event?.backgroundColor}
        >
            <Header />
            <main className="flex-1 py-12">
                <DashboardContentContainer>
                <div className="w-full sm:mx-auto sm:max-w-5xl space-y-8">
                    {/* Banner */}
                    {event?.bannerUrl && (
                        <div className="mb-6 rounded-lg overflow-hidden">
                            <img
                                src={event.bannerUrl}
                                alt="Event Banner"
                                className="w-full h-auto object-cover"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        </div>
                    )}

                    {/* Event or Unit Logo - Top of Page */}
                    {(event?.logoUrl || currentUnit?.settings?.logoUrl) && (
                        <div className="flex justify-center mb-6">
                            <img
                                src={event?.logoUrl || currentUnit?.settings?.logoUrl}
                                alt={`${event?.name || currentUnit?.name || 'Event'} Logo`}
                                className="w-1/4 max-w-xs h-auto object-contain"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        </div>
                    )}

                    {/* Back to Dashboard Button - Top (if logged in) */}
                    {currentUser && (
                        <div className="flex gap-2 mb-4">
                            <Button variant="outline" onClick={() => navigate('/dashboard')}>
                                Back to Dashboard
                            </Button>
                        </div>
                    )}

                    {/* Event Header with Combined Details */}
                    <Card className="shadow-lg">
                        <CardContent className="pt-6">
                            <div className="space-y-6">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                    <div className="flex-1 min-w-0">
                                    <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 break-words">{event.name}</h1>
                                    <div className="flex flex-wrap gap-6 text-base text-muted-foreground mb-6">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1.5 bg-primary/10 rounded-lg">
                                                <Calendar className="h-4 w-4 text-primary" />
                                            </div>
                                            <span className="font-medium">{formatDate(event.date)} at {formatTime(event.date)}</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <div className="p-1.5 bg-primary/10 rounded-lg">
                                                <MapPin className="h-4 w-4 text-primary" />
                                            </div>
                                            <VenueInfo
                                                unitId={unitId!}
                                                venueId={event.venueId}
                                                location={event.location}
                                                compact={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                    <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
                                        <Badge variant="outline" className="px-2 py-1 text-xs whitespace-nowrap">
                                            <Calendar className="h-3 w-3 mr-1" />
                                            RSVP: {formatDate(event.rsvpDeadline)}
                                        </Badge>
                                        <Badge variant="outline" className="px-2 py-1 text-xs whitespace-nowrap">
                                            <CreditCard className="h-3 w-3 mr-1" />
                                            Payment: {formatDate(event.paymentDeadline)}
                                        </Badge>
                                    </div>
                                    </div>

                                    {event.dresscode && (
                                    <div className="pt-4 border-t border-border/50">
                                        <div className="p-3 bg-background/50 rounded-lg inline-block">
                                            <span className="text-sm font-medium text-muted-foreground block mb-1">Dress Code</span>
                                            <span className="text-sm font-medium text-foreground">{event.dresscode}</span>
                                        </div>
                                </div>
                                )}

                                {event.customInstructions && (
                                    <div className="p-3 bg-background/50 rounded-lg">
                                        <span className="text-sm font-medium text-muted-foreground block mb-1">Additional Information</span>
                                        <span className="text-sm text-foreground">{event.customInstructions}</span>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {outstandingBalance > 0 && (
                        <Alert variant="warning">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                <AlertDescription>
                                    Your ticket is reserved, but your place is only confirmed once payment is complete.
                                    Use the payment section below.
                                </AlertDescription>
                            </div>
                        </Alert>
                    )}

                    {/* Your RSVP Card */}
                    <Card className="shadow-lg">
                        <CardHeader className="pb-6">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <CardTitle className="flex items-center gap-3 text-2xl">
                                    <div className="p-3 bg-primary/10 rounded-xl">
                                        <User className="h-6 w-6 text-primary" />
                                    </div>
                                    Your RSVP
                                </CardTitle>
                                <div className="flex flex-col items-end gap-3">
                                    <div className="flex flex-wrap justify-end gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleShowCheckInQRCode(rsvp || undefined)}
                                            className="gap-2"
                                            title="Show Check-in QR Code"
                                        >
                                            <QrCode className="h-4 w-4" />
                                            Check-in QR
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleOpenEditDetailsModal}
                                            className="gap-2"
                                        >
                                            <User className="h-4 w-4" />
                                            Edit Details
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleOpenEditTicketModal}
                                            className="gap-2"
                                        >
                                            <Ticket className="h-4 w-4" />
                                            Edit Ticket
                                        </Button>
                                        {rsvp.status === 'waitlist' && event?.eventStatus !== 'published' ? (
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={handleLeaveWaitlist}
                                                className="gap-2"
                                            >
                                                <XCircle className="h-4 w-4" />
                                                Leave Waitlist
                                            </Button>
                                        ) : (rsvp.status === 'confirmed' || (rsvp.status === 'waitlist' && event?.eventStatus === 'published')) ? (
                                            <Button
                                                type="button"
                                                variant="destructive"
                                                size="sm"
                                                onClick={handleCancelRSVP}
                                                className="gap-2 touch-manipulation"
                                            >
                                                <XCircle className="h-4 w-4" />
                                                Cancel RSVP
                                            </Button>
                                        ) : null}
                                    </div>
                                    <div className="flex gap-2 flex-wrap justify-end">
                                        {rsvp.status === 'waitlist' && event?.eventStatus !== 'published' ? (
                                            <Badge variant="secondary" className="gap-1 px-3 py-1">
                                                <Clock className="h-3 w-3" />
                                                On Waitlist
                                            </Badge>
                                        ) : !rsvp.paid && outstandingBalance > 0 ? (
                                            <Badge variant="secondary" className="gap-1 px-3 py-1 bg-orange-100 text-orange-800 border-orange-200">
                                                <Clock className="h-3 w-3" />
                                                Pending Payment
                                            </Badge>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-8">
                                {/* Personal Information Section */}
                                <div className="space-y-4">
                                    <div className="flex items-start justify-between">
                                        <div className="space-y-3">
                                            <div>
                                                <h3 className="text-xl font-bold text-foreground">{rsvp.name}</h3>
                                                <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                                    <div className="flex items-center gap-1">
                                                        <Award className="h-4 w-4" />
                                                        <span className="font-medium">{rsvp.rank}</span>
                                                    </div>
                                                    <span>•</span>
                                                    <span className="font-medium">{rsvp.unit}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Mail className="h-4 w-4" />
                                                <span>{rsvp.email}</span>
                                            </div>
                                            {rsvp.apologies && (
                                                <div className="mt-3 w-full">
                                                    <div className="w-full rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                                        <div className="flex items-start gap-3">
                                                            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
                                                            <div className="space-y-1">
                                                                <p className="font-semibold">Apologies recorded</p>
                                                                <p>
                                                                    You are currently marked as unable to attend. You can still invite guests or manage your RSVP.
                                                                    To rejoin the attendance list, update your ticket and meal choices.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Meal Preferences Section */}
                                {rsvp.responses && Object.keys(rsvp.responses).length > 0 && (
                                    <div className="bg-muted/50 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="p-1 bg-primary/10 rounded">
                                                <Ticket className="h-4 w-4 text-primary" />
                                            </div>
                                            <h4 className="font-semibold text-foreground">Ticket & Meal Preferences</h4>
                                        </div>
                                        <div className="space-y-4">
                                            {/* Display Tickets if using ticket system */}
                                            {rsvp.responses.Tickets && Array.isArray(rsvp.responses.Tickets) && event?.tickets && event.tickets.length > 0 ? (
                                                rsvp.responses.Tickets.map((ticket: any, ticketIndex: number) => {
                                                    const ticketType = event.tickets?.find(t => t.id === ticket.id);
                                                    const normalizedTicketData = normalizeTicketDataToFormatA(
                                                        rsvp.responses.TicketCourses?.[ticket.id]
                                                    );
                                                    return (
                                                        <div key={ticket.id || ticketIndex} className="border border-border/50 rounded-lg p-3 bg-background/50">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="font-medium text-foreground">{ticket.name || ticketType?.name || 'Ticket'}</span>
                                                                {ticket.qty > 1 && (
                                                                    <Badge variant="secondary" className="text-xs">
                                                                        Qty: {ticket.qty}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            {ticketType?.courses && ticketType.courses.length > 0 && normalizedTicketData ? (
                                                                <div className="mt-3">
                                                                    {Object.entries(normalizedTicketData).map(([instanceIndex, selections]) => (
                                                                        <div key={instanceIndex} className="space-y-2">
                                                                            {ticket.qty > 1 && (
                                                                                <span className="text-xs font-medium text-muted-foreground block mb-2">Ticket {instanceIndex}:</span>
                                                                            )}
                                                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                                                {ticketType.courses!.map((course) => {
                                                                                    const selectedOption = selections[course.id];
                                                                                    return selectedOption ? (
                                                                                        <div key={course.id} className="flex flex-col">
                                                                                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{course.name}</span>
                                                                                            <span className="text-sm font-medium text-foreground">{String(selectedOption)}</span>
                                                                                        </div>
                                                                                    ) : null;
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                            {rsvp.responses.Allergies && (
                                                                <div className="mt-2 pt-2 border-t border-border/30">
                                                                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Allergies</span>
                                                                    <span className="text-sm font-medium text-foreground ml-2">{String(rsvp.responses.Allergies)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                /* Fallback to legacy course/meal display */
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {Object.entries(rsvp.responses)
                                                        .filter(([key]) => key !== 'Tickets' && key !== 'TicketCourses')
                                                        .map(([key, value]) => (
                                                            <div key={key} className="flex flex-col">
                                                                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{key}</span>
                                                                <span className="text-sm font-medium text-foreground">{String(value)}</span>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}


                                {/* Payment Action removed from Your RSVP card */}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Payment Summary Card */}
                    <Card className="shadow-lg">
                        <CardHeader className="pb-6">
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-3 text-2xl">
                                    <div className="p-3 bg-blue-100 rounded-xl">
                                        <CreditCard className="h-6 w-6 text-blue-600" />
                                    </div>
                                    Payment Summary
                                </CardTitle>
                                <Dialog open={showPaymentHistoryModal} onOpenChange={setShowPaymentHistoryModal}>
                                    {useInvoiceForMyPaymentDisplay ? (
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm" className="flex items-center gap-2">
                                            <History className="h-4 w-4" />
                                            Payment History
                                        </Button>
                                    </DialogTrigger>
                                    ) : null}
                                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                                        <DialogHeader>
                                            <DialogTitle className="flex items-center gap-2">
                                                <Receipt className="h-5 w-5" />
                                                Payment History
                                            </DialogTitle>
                                            <DialogDescription>
                                                Complete history of all payments made for this event
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-4">
                                            {invoice?.paymentHistory && invoice.paymentHistory.length > 0 ? (
                                                <div className="space-y-3">
                                                    {invoice.paymentHistory.map((payment, index) => (
                                                        <Card key={index}>
                                                            <CardContent className="p-4">
                                                                <div className="flex items-start justify-between">
                                                                    <div className="space-y-2">
                                                                        <div className="flex items-center gap-2">
                                                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                                                            <span className="font-medium">Payment #{index + 1}</span>
                                                                            <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                                                                                Completed
                                                                            </Badge>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                                                            <div>
                                                                                <span className="text-muted-foreground">Amount:</span>
                                                                                <p className="font-medium">{formatCurrency(payment.amount)}</p>
                                                                            </div>
                                                                            <div>
                                                                                <span className="text-muted-foreground">Date:</span>
                                                                                <p className="font-medium">
                                                                                    {formatDate(payment.date)} at {formatTime(payment.date)}
                                                                                </p>
                                                                            </div>
                                                                            <div>
                                                                                <span className="text-muted-foreground">Method:</span>
                                                                                <p className="font-medium">{payment.method || 'Card Payment'}</p>
                                                                            </div>
                                                                            <div>
                                                                                <span className="text-muted-foreground">Card processing fee:</span>
                                                                                <p className="font-medium">{formatCurrency(payment.processingFee)}</p>
                                                                            </div>
                                                                        </div>
                                                                        {payment.stripePaymentIntentId && (
                                                                            <div className="pt-2 border-t">
                                                                                <span className="text-muted-foreground text-sm">Payment Intent ID:</span>
                                                                                <p className="font-mono text-sm text-muted-foreground">{payment.stripePaymentIntentId}</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <p className="text-lg font-bold text-green-600">
                                                                            {formatCurrency(payment.amount)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                    <div className="pt-4 border-t">
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-medium">Total Paid:</span>
                                                            <span className="text-xl font-bold text-green-600">
                                                                {formatCurrency(amountPaid)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-8">
                                                    <Receipt className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                                                    <p className="text-muted-foreground">No payment history available</p>
                                                    <p className="text-sm text-muted-foreground">Payments will appear here once they are processed</p>
                                                </div>
                                            )}
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </CardHeader>
                        
                        {/* Edit Details Modal */}
                        <Dialog open={showEditDetailsModal} onOpenChange={setShowEditDetailsModal}>
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                        <User className="h-5 w-5" />
                                        Edit Personal Details
                                    </DialogTitle>
                                    <DialogDescription>
                                        Update your personal information for {event?.name}
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-6 py-4">
                                    <div className="space-y-4">
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label htmlFor="edit-first-name">First name *</Label>
                                                <Input
                                                    id="edit-first-name"
                                                    type="text"
                                                    placeholder="John"
                                                    value={editDetailsForm.firstName}
                                                    onChange={(e) =>
                                                        setEditDetailsForm({ ...editDetailsForm, firstName: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="edit-last-name">Last name *</Label>
                                                <Input
                                                    id="edit-last-name"
                                                    type="text"
                                                    placeholder="Smith"
                                                    value={editDetailsForm.lastName}
                                                    onChange={(e) =>
                                                        setEditDetailsForm({ ...editDetailsForm, lastName: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="edit-email">Email *</Label>
                                            <Input
                                                id="edit-email"
                                                type="email"
                                                placeholder="john@example.com"
                                                value={editDetailsForm.email}
                                                onChange={(e) => setEditDetailsForm({ ...editDetailsForm, email: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="edit-rank">Prefix *</Label>
                                            <Select
                                                value={editDetailsForm.rank}
                                                onValueChange={(value) => setEditDetailsForm({ ...editDetailsForm, rank: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select your prefix" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {prefixSelectOptions.map((prefix) => (
                                                        <SelectItem key={prefix.id} value={prefix.name}>
                                                            {prefix.shortName
                                                                ? `${prefix.name} (${prefix.shortName})`
                                                                : prefix.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="edit-additionalRank">Rank</Label>
                                            <Input
                                                id="edit-additionalRank"
                                                type="text"
                                                placeholder="eg. ProvJGW / PPADoC / AGSec"
                                                value={editDetailsForm.additionalRank}
                                                onChange={(e) => setEditDetailsForm({ ...editDetailsForm, additionalRank: e.target.value })}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="edit-unit">Unit/Organization</Label>
                                            <Input
                                                id="edit-unit"
                                                type="text"
                                                placeholder="Veritas Unit No. 1234"
                                                value={editDetailsForm.unitName}
                                                onChange={(e) => setEditDetailsForm({ ...editDetailsForm, unitName: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button 
                                        type="button" 
                                        variant="outline" 
                                        onClick={() => setShowEditDetailsModal(false)}
                                        disabled={savingDetails}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="button" 
                                        onClick={handleSaveDetails}
                                        disabled={savingDetails}
                                    >
                                        {savingDetails ? 'Saving...' : 'Save Details'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        
                        {/* Edit Ticket Modal */}
                        <Dialog open={showEditTicketModal} onOpenChange={setShowEditTicketModal}>
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                        <Ticket className="h-5 w-5" />
                                        Edit Ticket & Meal Preferences
                                    </DialogTitle>
                                    <DialogDescription>
                                        Update your ticket selection and meal preferences for {event?.name}
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-6 py-4">
                                    {/* Ticket Selection and Information */}
                                    {event?.tickets && event.tickets.length > 0 ? (
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold">Select Ticket</h3>
                                            <div className="rounded-lg border p-4 space-y-4">
                                                <div className="grid gap-4 md:grid-cols-2">
                                                    <div className="space-y-2">
                                                        <Label htmlFor="ticket-type">Ticket Type *</Label>
                                                        <Select
                                                            value={selectedTicket?.id || ''}
                                                            onValueChange={handleTicketTypeChange}
                                                        >
                                                            <SelectTrigger id="ticket-type">
                                                                <SelectValue placeholder="Select a ticket type..." />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {inviterAccessibleTickets.map((ticket) => (
                                                                    <SelectItem key={ticket.id} value={ticket.id}>
                                                                        {ticket.name} - {formatCurrency(ticket.price || 0)}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    {selectedTicket && (() => {
                                                        const ticketType = event.tickets.find(t => t.id === selectedTicket.id);
                                                        if (!ticketType) return null;
                                                        
                                                        return (
                                                            <div className="space-y-2">
                                                                <Label>Price</Label>
                                                                <div className="rounded-md border px-3 py-2 text-sm bg-muted">{formatCurrency(ticketType.price || 0)}</div>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                                
                                                {/* Course Selections for selected ticket */}
                                                {selectedTicket && (() => {
                                                    const ticketType = event.tickets.find(t => t.id === selectedTicket.id);
                                                    if (!ticketType || !ticketType.courses || ticketType.courses.length === 0) return null;
                                                    
                                                    return (
                                                        <div className="space-y-4 pt-4 border-t">
                                                            <h4 className="font-semibold">Meal Preferences</h4>
                                                            {ticketType.courses.map((course) => {
                                                                const instanceIndex = 1; // Single ticket
                                                                const currentValue = ticketCourseSelections?.[selectedTicket.id]?.[instanceIndex]?.[course.id] || '';
                                                                
                                                                return (
                                                                    <div key={course.id} className="space-y-2">
                                                                        <Label>{course.name} *</Label>
                                                                        <Select
                                                                            value={currentValue}
                                                                            onValueChange={(value) => {
                                                                                setTicketCourseSelections((prev) => {
                                                                                    const existingTicket = prev[selectedTicket.id] || {};
                                                                                    const existingInstance = existingTicket[instanceIndex] || {};
                                                                                    return {
                                                                                        ...prev,
                                                                                        [selectedTicket.id]: {
                                                                                            ...existingTicket,
                                                                                            [instanceIndex]: { ...existingInstance, [course.id]: value },
                                                                                        },
                                                                                    };
                                                                                });
                                                                            }}
                                                                        >
                                                                            <SelectTrigger>
                                                                                <SelectValue placeholder="Select your choice..." />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                {(course.options || []).map((option) => (
                                                                                    <SelectItem key={option} value={option}>
                                                                                        {option}
                                                                                    </SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ) : null}

                                    {/* Legacy Course/Meal Selection */}
                                    {(!event?.tickets || event.tickets.length === 0) && (
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold">Meal Preferences</h3>
                                            
                                            {event?.courses && event.courses.length > 0 ? (
                                                event.courses.map((course) => (
                                                    <div key={course.id} className="space-y-2">
                                                        <Label htmlFor={`modal-course-${course.id}`}>{course.name} *</Label>
                                                        <Select
                                                            value={courseSelections[course.id] || ''}
                                                            onValueChange={(value) =>
                                                                setCourseSelections({
                                                                    ...courseSelections,
                                                                    [course.id]: value,
                                                                })
                                                            }
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="Select your choice..." />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {course.options.map((option) => (
                                                                    <SelectItem key={option} value={option}>
                                                                        {option}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="space-y-2">
                                                    <Label htmlFor="modal-mealChoice">Meal Choice *</Label>
                                                    <Select value={mealChoice} onValueChange={setMealChoice}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select your meal..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="Beef">Beef Wellington</SelectItem>
                                                            <SelectItem value="Salmon">Salmon Fillet</SelectItem>
                                                            <SelectItem value="Vegetarian">Vegetarian Option</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Allergies */}
                                    <div className="space-y-2">
                                        <Label htmlFor="modal-allergies">Allergies & Special Requirements</Label>
                                        <Textarea
                                            id="modal-allergies"
                                            placeholder="Please list any allergies or special dietary requirements..."
                                            rows={3}
                                            value={allergies}
                                            onChange={(e) => setAllergies(e.target.value)}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            This information will be shared with the kitchen staff to ensure your safety
                                        </p>
                                    </div>
                                </div>
                                <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowEditTicketModal(false)}
                                        disabled={savingTicket || submittingApologyFromModal}
                                    >
                                        Cancel
                                    </Button>
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleSubmitApologyFromModal}
                                            disabled={savingTicket || submittingApologyFromModal}
                                            className="bg-amber-100 text-amber-900 hover:bg-amber-200"
                                        >
                                            {submittingApologyFromModal ? 'Recording...' : 'Record Apologies Instead'}
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={handleSaveTicket}
                                            disabled={savingTicket || submittingApologyFromModal}
                                        >
                                            {savingTicket ? 'Saving...' : 'Update Ticket & Meals'}
                                        </Button>
                                    </div>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <CardContent>
                            <div className="space-y-6">
                                {invitedGuestInviterCoversMe && guestsCost === 0 && (
                                    <Alert className="border-green-200 bg-green-50 text-green-900">
                                        <CheckCircle className="h-4 w-4 text-green-700" />
                                        <AlertDescription>
                                            You have nothing to pay for this event. The person who invited you is covering your ticket.
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {/* Payment Metrics Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-200 rounded-lg">
                                                <Users className="h-5 w-5 text-blue-700" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-blue-800">Total Guests</p>
                                                <p className="text-2xl font-bold text-blue-900">{totalGuests}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-slate-200 rounded-lg">
                                                <CreditCard className="h-5 w-5 text-slate-700" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-slate-800">Total Amount</p>
                                                <p className="text-2xl font-bold text-slate-900">{formatCurrency(displayTotal)}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-green-200 rounded-lg">
                                                <CheckCircle className="h-5 w-5 text-green-700" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-green-800">Amount Paid</p>
                                                <p className="text-2xl font-bold text-green-900">{formatCurrency(amountPaid)}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className={`p-4 rounded-xl border ${
                                        outstandingBalance > 0
                                            ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'
                                            : outstandingBalance < 0
                                                ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'
                                                : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
                                    }`}>
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${
                                                outstandingBalance > 0
                                                    ? 'bg-orange-200'
                                                    : outstandingBalance < 0
                                                        ? 'bg-blue-200'
                                                        : 'bg-green-200'
                                            }`}>
                                                {outstandingBalance > 0 ? (
                                                    <Clock className="h-5 w-5 text-orange-700" />
                                                ) : outstandingBalance < 0 ? (
                                                    <CreditCard className="h-5 w-5 text-blue-700" />
                                                ) : (
                                                    <CheckCircle className="h-5 w-5 text-green-700" />
                                                )}
                                            </div>
                                            <div>
                                                <p className={`text-sm font-medium ${
                                                    outstandingBalance > 0
                                                        ? 'text-orange-800'
                                                        : outstandingBalance < 0
                                                            ? 'text-blue-800'
                                                            : 'text-green-800'
                                                }`}>
                                                    {outstandingBalance > 0
                                                        ? 'Outstanding'
                                                        : outstandingBalance < 0
                                                            ? 'Credit'
                                                            : 'Outstanding'}
                                                </p>
                                                <p className={`text-2xl font-bold ${
                                                    outstandingBalance > 0
                                                        ? 'text-orange-900'
                                                        : outstandingBalance < 0
                                                            ? 'text-blue-900'
                                                            : 'text-green-900'
                                                }`}>
                                                    {formatCurrency(Math.abs(outstandingBalance))}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Breakdown */}
                                <div className="bg-gradient-to-br from-muted/30 to-muted/60 rounded-xl p-6 border">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-primary/10 rounded-lg">
                                            <Receipt className="h-5 w-5 text-primary" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-foreground">Payment Breakdown</h4>
                                    </div>
                                    {event && isCreditRecoveryEnabledForEvent(event) && perSlotOnHostBill > 0 && (
                                        <p className="text-xs text-muted-foreground mb-3">
                                            {BOOKING_FEE_LABEL} for this event: {formatCurrency(perSlotOnHostBill)} per seat on
                                            your booking (organiser setting).
                                        </p>
                                    )}
                                    {event && rsvp ? (
                                        <div className="overflow-x-auto rounded-md border border-border bg-background">
                                            <table className="w-full min-w-[360px] text-sm">
                                                <thead>
                                                    <tr className="border-b border-border bg-muted/60 text-left">
                                                        <th className="p-2 font-medium">Guest</th>
                                                        <th className="p-2 font-medium">Ticket</th>
                                                        <th className="p-2 font-medium text-right">Ticket price</th>
                                                        <th className="p-2 font-medium text-right">{BOOKING_FEE_LABEL}</th>
                                                        <th className="p-2 font-medium text-right">Line total</th>
                                                        <th className="p-2 font-medium">Paid</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="border-b border-border">
                                                        <td className="p-2 align-top">
                                                            <span>
                                                                {[rsvp.rank, formatRsvpGuestName(rsvp)]
                                                                    .filter(Boolean)
                                                                    .join(' ')
                                                                    .trim() || formatRsvpGuestName(rsvp)}
                                                            </span>
                                                        </td>
                                                        <td className="p-2 align-top text-muted-foreground">
                                                            {describeRsvpTicketSelection(rsvp, event)}
                                                        </td>
                                                        <td className="p-2 align-top text-right tabular-nums">
                                                            {formatCurrency(mainRsvpTicketPrice)}
                                                        </td>
                                                        <td className="p-2 align-top text-right tabular-nums">
                                                            {formatCurrency(
                                                                billableRsvpCountForBookingFee > 0 && mainRsvpCost > 0
                                                                    ? perSlotOnHostBill
                                                                    : 0,
                                                            )}
                                                        </td>
                                                        <td className="p-2 align-top text-right tabular-nums font-medium">
                                                            {formatCurrency(
                                                                mainRsvpCost +
                                                                    (billableRsvpCountForBookingFee > 0 && mainRsvpCost > 0
                                                                        ? perSlotOnHostBill
                                                                        : 0),
                                                            )}
                                                        </td>
                                                        <td className="p-2 align-top">
                                                            {showMainLinePaid && (
                                                                <Badge
                                                                    variant="default"
                                                                    className="gap-1 px-2 py-0.5 bg-green-100 text-green-800 border-green-200"
                                                                >
                                                                    <CheckCircle className="h-3 w-3" />
                                                                    Paid
                                                                </Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {guestsInviterPaysFor.map((guest) => {
                                                        const guestTicket = ticketSubtotalForBreakdownRow(guest, event);
                                                        const guestBooking =
                                                            billableRsvpCountForBookingFee > 0 && guestTicket > 0
                                                                ? perSlotOnHostBill
                                                                : 0;
                                                        const guestLine = guestTicket + guestBooking;
                                                        return (
                                                            <tr key={guest.id} className="border-b border-border">
                                                                <td className="p-2 align-top">
                                                                    <span>
                                                                        {[guest.rank, formatRsvpGuestName(guest)]
                                                                            .filter(Boolean)
                                                                            .join(' ')
                                                                            .trim() || formatRsvpGuestName(guest)}
                                                                    </span>
                                                                </td>
                                                                <td className="p-2 align-top text-muted-foreground">
                                                                    {describeRsvpTicketSelection(guest, event)}
                                                                </td>
                                                                <td className="p-2 align-top text-right tabular-nums">
                                                                    {formatCurrency(guestTicket)}
                                                                </td>
                                                                <td className="p-2 align-top text-right tabular-nums">
                                                                    {formatCurrency(guestBooking)}
                                                                </td>
                                                                <td className="p-2 align-top text-right tabular-nums font-medium">
                                                                    {formatCurrency(guestLine)}
                                                                </td>
                                                                <td className="p-2 align-top">
                                                                    {guest.paid && (
                                                                        <Badge
                                                                            variant="default"
                                                                            className="gap-1 px-2 py-0.5 bg-green-100 text-green-800 border-green-200"
                                                                        >
                                                                            <CheckCircle className="h-3 w-3" />
                                                                            Paid
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {guestsPayingSeparately.map((guest) => {
                                                        const guestTicket = ticketSubtotalForBreakdownRow(guest, event);
                                                        const guestBooking = event
                                                            ? getInvoiceCreditRecoveryPounds(
                                                                  event,
                                                                  guestTicket,
                                                                  creditBookingFeePence,
                                                              )
                                                            : 0;
                                                        const guestLine = guestTicket + guestBooking;
                                                        const zeroPaymentRequired =
                                                            getRsvpExpectedAmount(guest, event) === 0;
                                                        const displayGuestPaid = guest.paid || zeroPaymentRequired;
                                                        return (
                                                            <tr
                                                                key={guest.id}
                                                                className="border-b border-border last:border-0 bg-muted/20"
                                                            >
                                                                <td className="p-2 align-top">
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <span>
                                                                            {[guest.rank, formatRsvpGuestName(guest)]
                                                                                .filter(Boolean)
                                                                                .join(' ')
                                                                                .trim() || formatRsvpGuestName(guest)}
                                                                        </span>
                                                                        <Badge
                                                                            variant="outline"
                                                                            className="gap-1 border-gray-300"
                                                                        >
                                                                            Guest Pays
                                                                        </Badge>
                                                                    </div>
                                                                </td>
                                                                <td className="p-2 align-top text-muted-foreground">
                                                                    {describeRsvpTicketSelection(guest, event)}
                                                                </td>
                                                                <td className="p-2 align-top text-right tabular-nums">
                                                                    {formatCurrency(guestTicket)}
                                                                </td>
                                                                <td className="p-2 align-top text-right tabular-nums">
                                                                    {formatCurrency(guestBooking)}
                                                                </td>
                                                                <td className="p-2 align-top text-right tabular-nums font-medium">
                                                                    {formatCurrency(guestLine)}
                                                                </td>
                                                                <td className="p-2 align-top">
                                                                    {displayGuestPaid ? (
                                                                        <Badge
                                                                            variant="default"
                                                                            className="gap-1 px-2 py-0.5 bg-green-100 text-green-800 border-green-200"
                                                                        >
                                                                            <CheckCircle className="h-3 w-3" />
                                                                            Guest Paid
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge
                                                                            variant="secondary"
                                                                            className="gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 border-orange-200"
                                                                        >
                                                                            <Clock className="h-3 w-3" />
                                                                            Pending
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="border-t border-border bg-muted/40 font-medium">
                                                        <td className="p-2" colSpan={2}>
                                                            Totals
                                                        </td>
                                                        <td className="p-2 text-right tabular-nums">
                                                            {formatCurrency(ticketSubtotalOnly)}
                                                        </td>
                                                        <td className="p-2 text-right tabular-nums">
                                                            {formatCurrency(bookingFeeTotal)}
                                                        </td>
                                                        <td className="p-2 text-right tabular-nums">
                                                            {formatCurrency(displayTotal)}
                                                        </td>
                                                        <td className="p-2" />
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">Loading pricing…</p>
                                    )}

                                    {guestsPayingSeparately.length > 0 && (
                                        <div className="p-3 mt-4 bg-blue-50 border border-blue-200 rounded-lg">
                                            <div className="flex items-center gap-2 text-blue-800">
                                                <Info className="h-4 w-4" />
                                                <span className="text-sm font-medium">
                                                    {guestsPayingSeparately.length} guest
                                                    {guestsPayingSeparately.length !== 1 ? 's' : ''} paying separately
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="border-t border-border/50 pt-4 mt-4">
                                        <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                                            <span className="text-lg font-bold text-foreground">Your Total</span>
                                            <span className="text-2xl font-bold text-primary">
                                                {formatCurrency(displayTotal)}
                                            </span>
                                        </div>
                                    </div>
                                </div>


                                {/* Pending verification / approved notice */}
                                {invoice?.status === 'pending_verification' && (
                                    <div className="pt-4 border-t">
                                        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
                                            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                                            <div className="space-y-1">
                                                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                                                    Payment awaiting approval
                                                </p>
                                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                                    Your bank transfer has been received and is pending review by the treasurer. You will be notified once it has been approved.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {invoice?.status === 'paid' && amountPaid > 0 && (
                                    <div className="pt-4 border-t">
                                        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
                                            <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
                                            <div className="space-y-1">
                                                <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                                                    Payment approved
                                                </p>
                                                <p className="text-xs text-green-700 dark:text-green-300">
                                                    Your payment of {formatCurrency(amountPaid)} has been approved and your booking is confirmed. Thank you.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                {outstandingBalance > 0 && invoice?.status !== 'pending_verification' && (
                                    <div className="pt-4 border-t">
                                        <Button
                                            type="button"
                                            className="w-full h-11 touch-manipulation"
                                            onClick={handlePayNow}
                                        >
                                            <CreditCard className="mr-2 h-4 w-4" />
                                            Pay Now
                                        </Button>
                                    </div>
                                )}

                            </div>
                        </CardContent>
                    </Card>

                    {/* Invited Guests Card */}
                    <Card>
                        <CardHeader className="pb-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2 text-xl">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <Users className="h-5 w-5 text-purple-600" />
                                        </div>
                                        Your Invited Guests ({invitedGuests.length})
                                    </CardTitle>
                                    <CardDescription>
                                        Manage your invited guests and their meal preferences
                                    </CardDescription>
                                    {invitedGuests.length > 0 && (
                                        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 text-sm">
                                            <div className="flex items-center gap-1">
                                                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                                <span className="text-muted-foreground">{invitedGuests.filter(g => g.status === 'pending').length} Pending</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                <span className="text-muted-foreground">{invitedGuests.filter(g => g.status === 'confirmed').length} Accepted</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                                <span className="text-muted-foreground">{invitedGuests.filter(g => g.status === 'declined').length} Declined</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2 w-full sm:w-auto sm:flex-row sm:flex-nowrap">
                                    <Button 
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowAddGuestModal(true)}
                                        className="flex items-center justify-center gap-2 w-full touch-manipulation sm:w-auto"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Guest
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={handleOpenGetLinkModal} className="w-full sm:w-auto">
                                        <Link className="mr-2 h-4 w-4" />
                                        Get Link
                                    </Button>
                                    <Button size="sm" onClick={() => setShowSendInvitesModal(true)} className="w-full sm:w-auto">
                                        <Mail className="mr-2 h-4 w-4" />
                                        Send Email Invites
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-0 sm:pt-2">
                            {invitedGuests.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                    <p className="mb-2">You haven't invited any guests yet</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {invitedGuests.map((guest) => (
                                        <Card key={guest.id} className="overflow-hidden border border-border/60 shadow-sm">
                                            <CardContent className="p-3 sm:p-4">
                                                <div className="space-y-4">
                                                    {/* Guest Info Header */}
                                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                {guest.status === 'confirmed' && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8"
                                                                        onClick={() => handleShowCheckInQRCode(guest)}
                                                                        title="Show Check-in QR Code for this guest"
                                                                    >
                                                                        <QrCode className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                                <h4 className="font-semibold text-lg">{guest.name}</h4>
                                                                <Badge 
                                                                    variant={
                                                                        guest.status === 'confirmed' ? 'default' :
                                                                        guest.status === 'pending' ? 'secondary' :
                                                                        'destructive'
                                                                    }
                                                                    className={`gap-1 ${
                                                                        guest.status === 'confirmed' ? 'bg-green-100 text-green-800 border-green-200' :
                                                                        guest.status === 'pending' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                                                        'bg-red-100 text-red-800 border-red-200'
                                                                    }`}
                                                                >
                                                                    {guest.status === 'confirmed' && <CheckCircle className="h-3 w-3" />}
                                                                    {guest.status === 'pending' && <Clock className="h-3 w-3" />}
                                                                    {guest.status === 'declined' && <XCircle className="h-3 w-3" />}
                                                                    <span className="capitalize">{guest.status}</span>
                                                                </Badge>
                                                            </div>
                                                            {guest.rank && guest.unit && (
                                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                    <Award className="h-4 w-4" />
                                                                    <span>{guest.rank} - {guest.unit}</span>
                                                                </div>
                                                            )}
                                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                <Mail className="h-4 w-4" />
                                                                <span>{guest.email}</span>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2 text-left sm:text-right">
                                                            {/* Payment Status */}
                                                            {guest.status === 'confirmed' && (
                                                                <>
                                                                    {/* Show who is paying */}
                                                                    {isInviterPayingForGuest(guest) ? (
                                                                        <Badge variant="default" className="gap-1 bg-blue-100 text-blue-800 border-blue-200">
                                                                            <span>You Pay</span>
                                                                        </Badge>
                                                                    ) : (
                                                                        <div className="flex items-center gap-1.5">
                                                                            <Badge variant="outline" className="gap-1 border-gray-300">
                                                                                <span>Guest Pays</span>
                                                                            </Badge>
                                                                            {/* Show guest payment status */}
                                                                    {(() => {
                                                                        const zeroPaymentRequired = getRsvpExpectedAmount(guest, event) === 0;
                                                                        const displayGuestPaid = guest.paid || zeroPaymentRequired;
                                                                        return displayGuestPaid ? (
                                                                            <Badge variant="default" className="gap-1 bg-green-100 text-green-800 border-green-200">
                                                                                <CheckCircle className="h-3 w-3" />
                                                                                Guest Paid
                                                                            </Badge>
                                                                        ) : (
                                                                            <Badge variant="secondary" className="gap-1 bg-orange-100 text-orange-800 border-orange-200">
                                                                                <Clock className="h-3 w-3" />
                                                                                Pending
                                                                            </Badge>
                                                                        );
                                                                    })()}
                                                                </div>
                                                                    )}
                                                                </>
                                                            )}
                                                            {/* Guest Cost */}
                                                            {guest.status === 'confirmed' && (() => {
                                                                const guestTicket = rsvpTicketSubtotal(guest);
                                                                const onHostBill = isInviterPayingForGuest(guest);
                                                                const feeShare = onHostBill
                                                                    ? billableRsvpCountForBookingFee > 0 &&
                                                                      ticketSubtotalForBreakdownRow(guest, event) > 0
                                                                        ? perSlotOnHostBill
                                                                        : 0
                                                                    : event
                                                                      ? getInvoiceCreditRecoveryPounds(
                                                                            event,
                                                                            guestTicket,
                                                                            creditBookingFeePence,
                                                                        )
                                                                      : 0;
                                                                const line = guestTicket + feeShare;
                                                                return (
                                                                    <div className="text-sm space-y-0.5">
                                                                        <div>
                                                                            <span className="text-muted-foreground">Ticket: </span>
                                                                            <span className="font-medium">
                                                                                {formatCurrency(guestTicket)}
                                                                            </span>
                                                                        </div>
                                                                        {feeShare > 0 && (
                                                                            <div>
                                                                                <span className="text-muted-foreground">
                                                                                    {BOOKING_FEE_LABEL}:{' '}
                                                                                </span>
                                                                                <span className="font-medium">
                                                                                    {formatCurrency(feeShare)}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        <div>
                                                                            <span className="text-muted-foreground">Total: </span>
                                                                            <span className="font-medium">
                                                                                {formatCurrency(line)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>

                                                    {/* Meal Preferences */}
                                                    {guest.status === 'confirmed' && guest.responses && Object.keys(guest.responses).length > 0 && (
                                                        <div className="bg-muted/50 rounded-lg p-3 sm:p-4">
                                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="p-1 bg-primary/10 rounded">
                                                                        <Ticket className="h-4 w-4 text-primary" />
                                                                    </div>
                                                                    <h5 className="font-semibold text-foreground">Ticket & Meal Preferences</h5>
                                                                </div>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleEditGuestMealChoices(guest.id)}
                                                                    className="h-6 px-2 text-xs self-start sm:self-auto"
                                                                >
                                                                    <Edit className="h-3 w-3 mr-1" />
                                                                    Edit
                                                                </Button>
                                                            </div>
                                                            
                                                            {editingGuest === guest.id ? (
                                                                <div className="space-y-3">
                                                                    {/* Ticket System */}
                                                                    {event?.tickets && event.tickets.length > 0 ? (
                                                                        <>
                                                                            {/* Ticket Type Selection */}
                                                                            <div className="space-y-1">
                                                                                <Label className="text-xs font-medium text-muted-foreground">
                                                                                    Ticket Type
                                                                                </Label>
                                                                                <Select
                                                                                    value={editingGuestTicketId}
                                                                                    onValueChange={(value) => {
                                                                                        setEditingGuestTicketId(value);
                                                                                        // Reset course selections when ticket changes
                                                                                        setEditingGuestTicketCourses({});
                                                                                        // Load existing course selections if available
                                                                                        const guestData = invitedGuests.find(g => g.id === guest.id);
                                                                                        if (guestData?.responses?.TicketCourses?.[value]) {
                                                                                            const normalized = normalizeTicketDataToFormatA(
                                                                                                guestData.responses.TicketCourses[value]
                                                                                            );
                                                                                            const instanceSelections =
                                                                                                normalized?.[DEFAULT_INSTANCE_INDEX] ?? {};
                                                                                            setEditingGuestTicketCourses(instanceSelections);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <SelectTrigger>
                                                                                        <SelectValue placeholder="Select ticket type..." />
                                                                                    </SelectTrigger>
                                                                                    <SelectContent>
                                                                                        {editGuestTicketOptions.map((ticket) => (
                                                                                            <SelectItem key={ticket.id} value={ticket.id}>
                                                                                                {ticket.name} - {formatCurrency(ticket.price || 0)}
                                                                                            </SelectItem>
                                                                                        ))}
                                                                                    </SelectContent>
                                                                                </Select>
                                                                            </div>

                                                                            {/* Course Selections for Selected Ticket */}
                                                                            {editingGuestTicketId && (() => {
                                                                                const selectedTicket = event.tickets.find(t => t.id === editingGuestTicketId);
                                                                                if (!selectedTicket || !selectedTicket.courses || selectedTicket.courses.length === 0) {
                                                                                    return null;
                                                                                }
                                                                                return (
                                                                                    <div className="space-y-2 pt-2 border-t">
                                                                                        {selectedTicket.courses.map((course) => (
                                                                                            <div key={course.id} className="space-y-1">
                                                                                                <Label className="text-xs font-medium text-muted-foreground">
                                                                                                    {course.name}
                                                                                                </Label>
                                                                                                <Select
                                                                                                    value={editingGuestTicketCourses[course.id] || ''}
                                                                                                    onValueChange={(value) => setEditingGuestTicketCourses({
                                                                                                        ...editingGuestTicketCourses,
                                                                                                        [course.id]: value
                                                                                                    })}
                                                                                                >
                                                                                                    <SelectTrigger>
                                                                                                        <SelectValue placeholder="Select option..." />
                                                                                                    </SelectTrigger>
                                                                                                    <SelectContent>
                                                                                                        {course.options.map((option) => (
                                                                                                            <SelectItem key={option} value={option}>
                                                                                                                {option}
                                                                                                            </SelectItem>
                                                                                                        ))}
                                                                                                    </SelectContent>
                                                                                                </Select>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </>
                                                                    ) : (
                                                                        /* Legacy Course/Meal System */
                                                                        <>
                                                                    {event?.courses && event.courses.length > 0 ? (
                                                                        event.courses.map((course) => (
                                                                            <div key={course.id} className="space-y-1">
                                                                                <Label className="text-xs font-medium text-muted-foreground">
                                                                                    {course.name}
                                                                                </Label>
                                                                                <Select
                                                                                    value={editingMealChoices[course.name] || ''}
                                                                                    onValueChange={(value) => setEditingMealChoices({
                                                                                        ...editingMealChoices,
                                                                                        [course.name]: value
                                                                                    })}
                                                                                >
                                                                                    <SelectTrigger>
                                                                                        <SelectValue placeholder="Select option..." />
                                                                                    </SelectTrigger>
                                                                                    <SelectContent>
                                                                                        {course.options.map((option) => (
                                                                                            <SelectItem key={option} value={option}>
                                                                                                {option}
                                                                                            </SelectItem>
                                                                                        ))}
                                                                                    </SelectContent>
                                                                                </Select>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div className="space-y-1">
                                                                            <Label className="text-xs font-medium text-muted-foreground">Meal Choice</Label>
                                                                            <Select
                                                                                value={editingMealChoices.Meal || ''}
                                                                                onValueChange={(value) => setEditingMealChoices({
                                                                                    ...editingMealChoices,
                                                                                    Meal: value
                                                                                })}
                                                                            >
                                                                                <SelectTrigger>
                                                                                    <SelectValue placeholder="Select meal..." />
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                    <SelectItem value="Beef">Beef Wellington</SelectItem>
                                                                                    <SelectItem value="Salmon">Salmon Fillet</SelectItem>
                                                                                    <SelectItem value="Vegetarian">Vegetarian Option</SelectItem>
                                                                                </SelectContent>
                                                                            </Select>
                                                                        </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {/* Payment Option */}
                                                                    <div className="space-y-2 pt-2 border-t">
                                                                        <Label className="text-xs font-medium text-muted-foreground">
                                                                            Payment
                                                                        </Label>
                                                                        {(() => {
                                                                            // Calculate guest cost based on selected ticket
                                                                            let guestPrice = 0;
                                                                            if (event?.tickets && event.tickets.length > 0) {
                                                                                const selectedTicket = editingGuestTicketId 
                                                                                    ? event.tickets.find(t => t.id === editingGuestTicketId)
                                                                                    : (guest.responses?.Tickets && Array.isArray(guest.responses.Tickets) && guest.responses.Tickets.length > 0)
                                                                                        ? event.tickets.find(t => t.id === (guest.responses.Tickets as any[])[0]?.id)
                                                                                        : null;
                                                                                guestPrice = selectedTicket ? (selectedTicket.price || 0) : 0;
                                                                            } else if (event?.courses && event.courses.length > 0) {
                                                                                // Legacy pricing - use a default or calculate from courses
                                                                                guestPrice = 0; // You may want to add pricing logic here
                                                                            }
                                                                            
                                                                            return (
                                                                                <>
                                                                                    <label
                                                                                        htmlFor={`inviter-pays-${guest.id}`}
                                                                                        className="flex cursor-pointer items-center gap-3 rounded-md py-2 touch-manipulation"
                                                                                    >
                                                                                        <input
                                                                                            type="radio"
                                                                                            id={`inviter-pays-${guest.id}`}
                                                                                            name={`payment-${guest.id}`}
                                                                                            checked={editingGuestInviterPaysFor}
                                                                                            onChange={() => setEditingGuestInviterPaysFor(true)}
                                                                                            className="h-5 w-5 shrink-0"
                                                                                        />
                                                                                        <div className="flex flex-1 items-center justify-between gap-3">
                                                                                            <span className="text-sm">I will pay for this guest</span>
                                                                                            <span className="font-medium text-blue-600 text-sm">
                                                                                                {formatCurrency(guestPrice)}
                                                                                            </span>
                                                                                        </div>
                                                                                    </label>
                                                                                    <label
                                                                                        htmlFor={`guest-pays-${guest.id}`}
                                                                                        className="flex cursor-pointer items-center gap-3 rounded-md py-2 touch-manipulation"
                                                                                    >
                                                                                        <input
                                                                                            type="radio"
                                                                                            id={`guest-pays-${guest.id}`}
                                                                                            name={`payment-${guest.id}`}
                                                                                            checked={!editingGuestInviterPaysFor}
                                                                                            onChange={() => setEditingGuestInviterPaysFor(false)}
                                                                                            className="h-5 w-5 shrink-0"
                                                                                        />
                                                                                        <div className="flex flex-1 items-center justify-between gap-3">
                                                                                            <span className="text-sm">Guest will pay for themselves</span>
                                                                                            <span className="font-medium text-green-600 text-sm">
                                                                                                {formatCurrency(guestPrice)}
                                                                                            </span>
                                                                                        </div>
                                                                                    </label>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                    <div className="flex gap-2 pt-2">
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleSaveGuestMealChoices(guest.id)}
                                                                            className="h-7 px-3 text-xs"
                                                                        >
                                                                            <Save className="h-3 w-3 mr-1" />
                                                                            Save
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={handleCancelEditMealChoices}
                                                                            className="h-7 px-3 text-xs"
                                                                        >
                                                                            <X className="h-3 w-3 mr-1" />
                                                                            Cancel
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="space-y-4">
                                                                    {/* Display Tickets if present */}
                                                                    {guest.responses?.Tickets && Array.isArray(guest.responses.Tickets) && guest.responses.Tickets.length > 0 ? (
                                                                        guest.responses.Tickets.map((ticket: any, ticketIndex: number) => {
                                                                            const ticketType = event?.tickets?.find(t => t.id === ticket.id);
                                                                            const normalizedTicketData = normalizeTicketDataToFormatA(
                                                                                guest.responses.TicketCourses?.[ticket.id]
                                                                            );
                                                                            return (
                                                                                <div key={ticket.id || ticketIndex} className="border border-border/50 rounded-lg p-3 bg-background/50">
                                                                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                                                                                        <span className="font-medium text-foreground">{ticket.name || ticketType?.name || 'Ticket'}</span>
                                                                                        {ticket.qty > 1 && (
                                                                                            <Badge variant="secondary" className="text-xs">
                                                                                                Qty: {ticket.qty}
                                                                                            </Badge>
                                                                                        )}
                                                                                    </div>
                                                                                    {ticketType?.courses && ticketType.courses.length > 0 && normalizedTicketData ? (
                                                                                        <div className="mt-3">
                                                                                            {Object.entries(normalizedTicketData).map(([instanceIndex, selections]) => (
                                                                                                <div key={instanceIndex} className="space-y-2">
                                                                                                    {ticket.qty > 1 && (
                                                                                                        <span className="text-xs font-medium text-muted-foreground block mb-2">Ticket {instanceIndex}:</span>
                                                                                                    )}
                                                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                                                                        {ticketType.courses!.map((course) => {
                                                                                                            const selectedOption = selections[course.id];
                                                                                                            return selectedOption ? (
                                                                                                                <div key={course.id} className="flex flex-col">
                                                                                                                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{course.name}</span>
                                                                                                                    <span className="text-sm font-medium text-foreground">{String(selectedOption)}</span>
                                                                                                                </div>
                                                                                                            ) : null;
                                                                                                        })}
                                                                                                    </div>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    ) : null}
                                                                                    {guest.responses.Allergies && (
                                                                                        <div className="mt-2 pt-2 border-t border-border/30">
                                                                                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Allergies</span>
                                                                                            <span className="text-sm font-medium text-foreground ml-2">{String(guest.responses.Allergies)}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })
                                                                    ) : (
                                                                        /* Fallback to legacy course/meal display */
                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                                                            {Object.entries(guest.responses)
                                                                                .filter(([key]) => key !== 'Tickets' && key !== 'TicketCourses' && key !== 'Allergies')
                                                                                .map(([key, value]) => (
                                                                                    <div key={key} className="flex flex-col">
                                                                                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{key}</span>
                                                                                        <span className="text-sm font-medium text-foreground">{String(value)}</span>
                                                                                    </div>
                                                                                ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Action Buttons */}
                                                    <div className="flex gap-2 pt-3 border-t">
                                                        {guest.status === 'pending' && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm text-muted-foreground">
                                                                    Waiting for response...
                                                                </span>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleResendInvite(guest)}
                                                                    disabled={resendingInviteGuestId === guest.id}
                                                                >
                                                                    <Mail className="h-4 w-4 mr-1" />
                                                                    {resendingInviteGuestId === guest.id ? 'Resending...' : 'Resend Invite'}
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleCancelInvite(guest.id, guest.name)}
                                                                    className="text-destructive hover:text-destructive"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                                    Cancel Invitation
                                                                </Button>
                                                            </div>
                                                        )}
                                                        {guest.status === 'declined' && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm text-muted-foreground">
                                                                    Guest declined the invitation
                                                                </span>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleCancelInvite(guest.id, guest.name)}
                                                                    className="text-muted-foreground"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        )}
                                                        {guest.status === 'confirmed' && (
                                                            <div className="flex items-center gap-2">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveGuest(guest)}
                                                                    disabled={removingGuestId === guest.id}
                                                                    className="text-destructive hover:text-destructive"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                                    {removingGuestId === guest.id ? 'Removing Guest...' : 'Remove Guest'}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Send Invites Modal */}
                    <SendInvitesModal
                        open={showSendInvitesModal}
                        onOpenChange={setShowSendInvitesModal}
                        onSendInvites={handleSendInvites}
                        eventName={event?.name || ''}
                        inviteTicketOptions={sendInvitesTicketOptions}
                    />

                    <Dialog open={showGetLinkModal} onOpenChange={setShowGetLinkModal}>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader>
                                <DialogTitle>Get Personalised Invite Link</DialogTitle>
                                <DialogDescription>
                                    Choose a ticket type, generate a short link when available, and copy any previously generated links.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-5">
                                <div className="space-y-2">
                                    <Label>Ticket Type</Label>
                                    <Select value={selectedInviteTicketId} onValueChange={setSelectedInviteTicketId}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select a ticket type..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {inviterAccessibleTickets.map((ticket) => (
                                                <SelectItem key={ticket.id} value={ticket.id}>
                                                    {ticket.name} - {formatCurrency(ticket.price || 0)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Payment Option</Label>
                                    <label
                                        htmlFor="invite-link-inviter-pays"
                                        className="flex cursor-pointer items-center gap-3 rounded-md border border-input px-3 py-3 touch-manipulation"
                                    >
                                        <input
                                            type="checkbox"
                                            id="invite-link-inviter-pays"
                                            checked={inviteLinkInviterPaysFor}
                                            onChange={(e) => setInviteLinkInviterPaysFor(e.target.checked)}
                                            className="h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm font-medium">I will pay for this guest</span>
                                    </label>
                                </div>

                                <div className="rounded-md border">
                                    <div className="px-4 py-3 border-b font-medium text-sm">Your Previously Generated Links</div>
                                    <div className="max-h-72 overflow-y-auto">
                                        {loadingPersonalLinks || (showGetLinkModal && authLoading) ? (
                                            <div className="px-4 py-3 text-sm text-muted-foreground">Loading links...</div>
                                        ) : personalInviteLinks.length === 0 ? (
                                            <div className="px-4 py-3 text-sm text-muted-foreground">No previous links found.</div>
                                        ) : (
                                            personalInviteLinks.map((link) => {
                                                const ticketName = inviterAccessibleTickets.find((t) => t.id === link.ticketIds[0])?.name
                                                    || event?.tickets?.find((t) => t.id === link.ticketIds[0])?.name
                                                    || 'Ticket';
                                                return (
                                                    <div key={link.id} className="px-4 py-3 border-b last:border-b-0">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium">
                                                                    {ticketName}
                                                                    <span className="text-muted-foreground font-normal">
                                                                        {' '}
                                                                        · {link.inviterPaysFor ? 'You pay' : 'Guest pays'}
                                                                    </span>
                                                                </div>
                                                                <div className="text-xs text-muted-foreground truncate">{link.shortUrl || link.link}</div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {formatDate(link.createdAt)} · {link.clickCount} click{link.clickCount === 1 ? '' : 's'}
                                                                </div>
                                                            </div>
                                                            <Button variant="outline" size="sm" onClick={() => handleCopyExistingPersonalLink(link)}>
                                                                <Copy className="h-4 w-4 mr-1" />
                                                                Copy
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowGetLinkModal(false)}>
                                    Close
                                </Button>
                                <Button onClick={handleGeneratePersonalInviteLink} disabled={!selectedInviteTicketId || generatingPersonalLink}>
                                    {generatingPersonalLink ? 'Generating...' : 'Generate & Copy Link'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Add Guest Manually Modal */}
                    <Dialog open={showAddGuestModal} onOpenChange={setShowAddGuestModal}>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Plus className="h-5 w-5" />
                                    Add Guest Manually
                                </DialogTitle>
                                <DialogDescription>
                                    Add a guest to the event and complete their RSVP on their behalf
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-6">
                                {/* Personal Information */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-lg">Personal Information</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="guest-first-name">
                                                    First Name <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="guest-first-name"
                                                    value={newGuestForm.firstName}
                                                    onChange={(e) => setNewGuestForm({ ...newGuestForm, firstName: e.target.value })}
                                                    placeholder="John"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="guest-last-name">
                                                    Last Name <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="guest-last-name"
                                                    value={newGuestForm.lastName}
                                                    onChange={(e) => setNewGuestForm({ ...newGuestForm, lastName: e.target.value })}
                                                    placeholder="Smith"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="guest-email">
                                                    Email Address
                                                </Label>
                                                <Input
                                                    id="guest-email"
                                                    type="email"
                                                    value={newGuestForm.email}
                                                    onChange={(e) => setNewGuestForm({ ...newGuestForm, email: e.target.value })}
                                                    placeholder="john@example.com (optional)"
                                                />
                                            </div>
                                            {affiliationRequired && (
                                                <>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="guest-prefix">
                                                            Prefix
                                                        </Label>
                                                        <Select
                                                            value={newGuestForm.rank}
                                                            onValueChange={(value) => setNewGuestForm({ ...newGuestForm, rank: value })}
                                                        >
                                                            <SelectTrigger id="guest-prefix">
                                                                <SelectValue placeholder="Select prefix" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {prefixSelectOptions.map((prefix) => (
                                                                    <SelectItem key={prefix.id} value={prefix.name}>
                                                                        {prefix.shortName
                                                                            ? `${prefix.name} (${prefix.shortName})`
                                                                            : prefix.name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="guest-additional-rank">
                                                            Rank
                                                        </Label>
                                                        <Input
                                                            id="guest-additional-rank"
                                                            value={newGuestForm.additionalRank}
                                                            onChange={(e) => setNewGuestForm({ ...newGuestForm, additionalRank: e.target.value })}
                                                            placeholder="eg. ProvJGW / PPADoC / AGSec"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="guest-unit">
                                                            Unit/Organization
                                                        </Label>
                                                        <Input
                                                            id="guest-unit"
                                                            value={newGuestForm.unit}
                                                            onChange={(e) => setNewGuestForm({ ...newGuestForm, unit: e.target.value })}
                                                            placeholder="Veritas Unit No. 1234"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Meal Preferences */}
                                {event?.tickets && event.tickets.length > 0 ? (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-lg">Ticket & Meal Preferences</CardTitle>
                                            <CardDescription>
                                                Select ticket type and meal options for this guest
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div className="space-y-2">
                                                    <Label htmlFor="guest-ticket-type">
                                                        Ticket Type <span className="text-red-500">*</span>
                                                    </Label>
                                                    <Select
                                                        value={newGuestForm.ticketTypeId}
                                                        onValueChange={(ticketId) => {
                                                            const ticket = event.tickets?.find(t => t.id === ticketId);
                                                            setNewGuestForm({
                                                                ...newGuestForm,
                                                                ticketTypeId: ticketId,
                                                                ticketPrice: ticket?.price || 0,
                                                                ticketCourseSelections: {}, // Reset course selections when ticket changes
                                                            });
                                                        }}
                                                    >
                                                        <SelectTrigger id="guest-ticket-type">
                                                            <SelectValue placeholder="Select ticket type" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {inviterAccessibleTickets.map((t) => (
                                                                <SelectItem key={t.id} value={t.id}>
                                                                    {t.name} - {formatCurrency(t.price || 0)}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Price</Label>
                                                    <div className="rounded-md border px-3 py-2 text-sm bg-muted">
                                                        {(() => {
                                                            const selectedTicket = event.tickets?.find(t => t.id === newGuestForm.ticketTypeId);
                                                            return formatCurrency(selectedTicket?.price || 0);
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Course selections for selected ticket */}
                                            {(() => {
                                                const selectedTicket = event.tickets?.find(t => t.id === newGuestForm.ticketTypeId);
                                                if (!selectedTicket || !selectedTicket.courses || selectedTicket.courses.length === 0) {
                                                    return null;
                                                }
                                                
                                                return (
                                                    <div className="space-y-4 pt-2">
                                                        <Label>Meal Choices</Label>
                                                        {selectedTicket.courses.map((course) => (
                                                            <div key={course.id} className="space-y-2">
                                                                <Label htmlFor={`guest-ticket-course-${course.id}`}>
                                                                    {course.name} <span className="text-red-500">*</span>
                                                                </Label>
                                                                <Select
                                                                    value={newGuestForm.ticketCourseSelections[course.id] || ''}
                                                                    onValueChange={(value) => setNewGuestForm({
                                                                        ...newGuestForm,
                                                                        ticketCourseSelections: {
                                                                            ...newGuestForm.ticketCourseSelections,
                                                                            [course.id]: value,
                                                                        },
                                                                    })}
                                                                >
                                                                    <SelectTrigger id={`guest-ticket-course-${course.id}`}>
                                                                        <SelectValue placeholder="Select option..." />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {(course.options || []).map((option) => (
                                                                            <SelectItem key={option} value={option}>
                                                                                {option}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                            
                                            <div className="space-y-2">
                                                <Label htmlFor="guest-allergies">Allergies & Special Requirements</Label>
                                                <Textarea
                                                    id="guest-allergies"
                                                    value={newGuestForm.allergies}
                                                    onChange={(e) => setNewGuestForm({ ...newGuestForm, allergies: e.target.value })}
                                                    placeholder="Please list any allergies or special dietary requirements..."
                                                    rows={3}
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                ) : event?.courses && event.courses.length > 0 ? (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-lg">Meal Preferences</CardTitle>
                                            <CardDescription>
                                                Select meal options for this guest
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {event.courses.map((course) => (
                                                <div key={course.id} className="space-y-2">
                                                    <Label htmlFor={`guest-course-${course.id}`}>
                                                        {course.name} <span className="text-red-500">*</span>
                                                    </Label>
                                                    <Select
                                                        value={newGuestForm.courseSelections[course.id] || ''}
                                                        onValueChange={(value) => setNewGuestForm({
                                                            ...newGuestForm,
                                                            courseSelections: {
                                                                ...newGuestForm.courseSelections,
                                                                [course.id]: value
                                                            }
                                                        })}
                                                    >
                                                        <SelectTrigger id={`guest-course-${course.id}`}>
                                                            <SelectValue placeholder="Select option..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {course.options.map((option) => (
                                                                <SelectItem key={option} value={option}>
                                                                    {option}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            ))}
                                            <div className="space-y-2">
                                                <Label htmlFor="guest-allergies">Allergies & Special Requirements</Label>
                                                <Textarea
                                                    id="guest-allergies"
                                                    value={newGuestForm.allergies}
                                                    onChange={(e) => setNewGuestForm({ ...newGuestForm, allergies: e.target.value })}
                                                    placeholder="Please list any allergies or special dietary requirements..."
                                                    rows={3}
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>
                                ) : null}

                                {/* Payment Information */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-lg">Payment Information</CardTitle>
                                        <CardDescription>
                                            Who will pay for this guest's attendance?
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-3">
                                            {(() => {
                                                // Calculate price: ticket price from event configuration (pricing is now exclusively ticket-based)
                                                let guestPrice = 0;
                                                if (event?.tickets && event.tickets.length > 0 && newGuestForm.ticketTypeId) {
                                                    const ticketType = event.tickets.find(t => t.id === newGuestForm.ticketTypeId);
                                                    guestPrice = ticketType ? (ticketType.price || 0) : 0;
                                                }
                                                // If no ticket selected, price is 0 (ticket-based pricing is required)
                                                
                                                return (
                                                    <>
                                            <label
                                                htmlFor="inviter-pays"
                                                className="flex cursor-pointer items-center gap-3 rounded-md py-3 touch-manipulation"
                                            >
                                                <input
                                                    type="radio"
                                                    id="inviter-pays"
                                                    name="payment"
                                                    checked={newGuestForm.inviterPaysFor}
                                                    onChange={() =>
                                                        setNewGuestForm((prev) => ({ ...prev, inviterPaysFor: true }))
                                                    }
                                                    className="h-5 w-5 shrink-0"
                                                />
                                                <div className="flex flex-1 items-center justify-between gap-3">
                                                    <span>I will pay for this guest</span>
                                                    <span className="font-medium text-blue-600">
                                                        {formatCurrency(guestPrice)}
                                                    </span>
                                                </div>
                                            </label>
                                            <label
                                                htmlFor="guest-pays"
                                                className="flex cursor-pointer items-center gap-3 rounded-md py-3 touch-manipulation"
                                            >
                                                <input
                                                    type="radio"
                                                    id="guest-pays"
                                                    name="payment"
                                                    checked={!newGuestForm.inviterPaysFor}
                                                    onChange={() =>
                                                        setNewGuestForm((prev) => ({ ...prev, inviterPaysFor: false }))
                                                    }
                                                    className="h-5 w-5 shrink-0"
                                                />
                                                <div className="flex flex-1 items-center justify-between gap-3">
                                                    <span>Guest will pay for themselves</span>
                                                    <span className="font-medium text-green-600">
                                                        {formatCurrency(guestPrice)}
                                                    </span>
                                                </div>
                                            </label>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-4 border-t">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowAddGuestModal(false)}
                                        disabled={addingGuest}
                                        className="flex-1 touch-manipulation"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={handleAddManualGuest}
                                        disabled={addingGuest}
                                        className="flex-1 touch-manipulation"
                                    >
                                        <Plus className="mr-2 h-4 w-4" />
                                        {addingGuest ? 'Adding...' : 'Add Guest'}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
                </DashboardContentContainer>
            </main>

            {/* RSVP Cancellation Modal */}
            <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <XCircle className="h-5 w-5 text-destructive" />
                            Cancel RSVP
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to cancel your RSVP for "{event?.name}"? 
                            The lodge will be notified of your cancellation.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="cancellation-reason">
                                Reason for cancellation (optional)
                            </Label>
                            <Textarea
                                id="cancellation-reason"
                                placeholder="Please provide a reason for cancelling your RSVP..."
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                                className="mt-1"
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setShowCancelModal(false);
                                setCancellationReason('');
                            }}
                            disabled={cancelling}
                            className="touch-manipulation"
                        >
                            Keep RSVP
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleSubmitCancellation}
                            disabled={cancelling}
                            className="touch-manipulation"
                        >
                            {cancelling ? 'Cancelling...' : 'Cancel RSVP'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Check-in QR Code Dialog */}
            <Dialog open={showQRCodeDialog} onOpenChange={setShowQRCodeDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {qrCodeForGuest ? `Check-in QR Code - ${qrCodeForGuest.name}` : 'Check-in QR Code'}
                        </DialogTitle>
                        <DialogDescription>
                            {qrCodeForGuest 
                                ? `Scan this QR code to check in ${qrCodeForGuest.name} at the event`
                                : 'Scan this QR code to access the check-in page for this event'
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        {qrCodeUrl && (
                            <div className="flex flex-col items-center">
                                <img src={qrCodeUrl} alt="Check-in QR Code" className="mb-4" />
                                <p className="text-sm text-muted-foreground text-center mb-4">
                                    {qrCodeForGuest
                                        ? `This QR code can be scanned to check in ${qrCodeForGuest.name}`
                                        : 'Organizers can scan this QR code to quickly access the check-in page'
                                    }
                                </p>
                                
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                        const link = document.createElement('a');
                                        const fileName = qrCodeForGuest
                                            ? `check-in-${qrCodeForGuest.name}-QR-Code.png`
                                            : `check-in-${event?.name || 'event'}-QR-Code.png`;
                                        link.download = fileName;
                                        link.href = qrCodeUrl;
                                        link.click();
                                    }}
                                >
                                    <QrCode className="mr-2 h-4 w-4" />
                                    Download QR Code
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </EventPageBackground>
    );
}


